import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Search, X, Loader2, Building2, MapPin, LocateFixed, Clock, Truck, Star, SlidersHorizontal, Navigation, CalendarCheck, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { InvestModal } from './InvestLanguageFlags';
import { EVENT_CATEGORIES } from '../lib/eventData';
import { COUNTRY_SHAPES } from '../lib/countryShapes';

// Business filter option taxonomy (matches /api/map/businesses properties).
const ACTIVITY_OPTS = [
  { key: 'eat',           lv: 'Paēst & Izbaudīt', en: 'Eat & Enjoy', ru: 'Поесть и отдохнуть',    color: '#FF9F1C' },
  { key: 'entertainment', lv: 'Izklaide & Atpūta', en: 'Entertainment', ru: 'Развлечения', color: '#EC4899' },
  { key: 'shop',          lv: 'Iepirkties',        en: 'Shopping', ru: 'Покупки',      color: '#2563EB' },
  { key: 'service',       lv: 'Pakalpojumi',       en: 'Services', ru: 'Услуги',      color: '#4A6B53' },
];
const DEAL_OPTS = [
  { key: 'buy_now', lv: 'Pirkt uzreiz',      en: 'Buy now', ru: 'Купить сразу' },
  { key: 'reserve', lv: 'Rezervēt laiku',    en: 'Reserve', ru: 'Забронировать' },
  { key: 'visit',   lv: 'Apmeklēt klātienē', en: 'Visit in person', ru: 'Посетить лично' },
];
const NEAR_OPTS = [{ m: 500, l: '500 m' }, { m: 2000, l: '2 km' }, { m: 5000, l: '5 km' }];

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// OpenFreeMap (OSM vector, OSM-Bright-style "liberty") proxied through our own
// /api/map/* so every request is same-origin — renders inside the Emergent
// preview iframe and behind ad-blockers / firewalls, not just on the open web.
const DEFAULT_STYLE = `${process.env.REACT_APP_BACKEND_URL}/api/map/style`;

const LATVIA_CENTER = [24.6, 56.88];
const LATVIA_BOUNDS = [[20.95, 55.65], [28.25, 58.10]];

// Browse "places" — every administrative place shown as a small coloured dot
// (no misleading proximity-count clusters). Numbers live in the ACTIVITY layer.
// Dot radius grows with zoom so places stay easy to tap when zoomed in, and
// labels (names) appear early enough to read while zooming into an area.
// Zoom-based progressive disclosure bands (MapLibre continuous zoom).
// Latvia full-country view ≈ zoom 6.4. Layers cross-fade between bands so the
// map reveals: Regions (far) → Cities → Districts → Villages (close).
const REGION_MAX_ZOOM = 7.2;   // regions + country invest overlays visible below this; city composites appear from here onward
const DISTRICT_COMPOSITE_MIN = 8.3; // district composite clusters appear from the district band onward
const REGION_CLICK_ZOOM = 7.9; // clicking a region eases here — city bubbles appear so the user drills further by clicking the big numbers
// Layers are ADDITIVE: each fades in at its band and STAYS visible when zooming
// closer, so cities/districts/villages stack (they no longer disappear on zoom-in).
const CITY_OPACITY = ['interpolate', ['linear'], ['zoom'], 6.9, 0, 7.3, 0.95];
const DISTRICT_OPACITY = ['interpolate', ['linear'], ['zoom'], 8.0, 0, 8.5, 0.9];
const VILLAGE_OPACITY = ['interpolate', ['linear'], ['zoom'], 9.0, 0, 9.5, 0.85];

const PLACE_LAYERS = [
  { key: 'cities',    locType: 'city',     types: ['city'], color: '#D97736',
    labelLV: 'Pilsētas',         labelEN: 'Cities', labelRU: 'Города',
    radius: ['interpolate', ['linear'], ['zoom'], 5, 3.5, 9, 5.5, 12, 7.5, 15, 10],
    opacityExpr: CITY_OPACITY, labelMinZoom: 7.3 },
  { key: 'districts', locType: 'district', types: ['district'], color: '#2563EB',
    labelLV: 'Mikrorajoni',      labelEN: 'Districts', labelRU: 'Районы',
    radius: ['interpolate', ['linear'], ['zoom'], 7, 3, 10, 5.5, 13, 8, 15, 10],
    opacityExpr: DISTRICT_OPACITY, labelMinZoom: 8.5 },
  { key: 'villages',  locType: 'village',  types: ['village'], color: '#4A6B53',
    labelLV: 'Ciemi',            labelEN: 'Villages', labelRU: 'Сёла',
    radius: ['interpolate', ['linear'], ['zoom'], 8, 2.6, 11, 5, 13, 7.5, 15, 10],
    opacityExpr: VILLAGE_OPACITY, labelMinZoom: 9.5 },
];

const ACTIVITY_COLOR = '#FF9F1C';
const COMPANY_COLOR = '#7C3AED';
const EVENTS_COLOR = '#E11D48';

// Two clear groups:
//   • Geography (base) — always available, ON by default: Cities, Districts, Villages.
//   • Activities (dynamic) — OFF by default, user toggles: Events, Companies (+future).
const FILTERS = [
  ...PLACE_LAYERS.map((p) => ({ key: p.key, labelLV: p.labelLV, labelEN: p.labelEN, labelRU: p.labelRU, color: p.color, group: 'geo' })),
  { key: 'events',    labelLV: 'Pasākumi', labelEN: 'Events', labelRU: 'Мероприятия',    color: EVENTS_COLOR,  group: 'activity' },
  { key: 'companies', labelLV: 'Uzņēmumi', labelEN: 'Companies', labelRU: 'Компании', color: COMPANY_COLOR, group: 'activity' },
];

const LAYER_GROUPS = {
  companies: ['companies-clusters', 'companies-cluster-count', 'companies-pulse', 'companies-point', 'companies-label'],
  events: ['events-clusters', 'events-cluster-count', 'events-pulse', 'events-point', 'events-label'],
  cities: ['cities-point', 'cities-label', 'cities-count'],
  districts: ['districts-point', 'districts-label', 'districts-count'],
  villages: ['villages-point', 'villages-label', 'villages-count'],
};

const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Activity layers are added to the map but start HIDDEN; the base geography
// (cities/districts/villages) is the default view. Users toggle activities on.
const DEFAULT_OFF = ['events', 'companies'];

export default forwardRef(function VectorMap({
  height = '100%',
  fitLatvia = true,
  initialCenter = LATVIA_CENTER,
  initialZoom = 6.4,
  showFilters = true,
  showSearch = true,
  showLocate = true,
  enabledLayers = ['companies', 'events', 'cities', 'districts', 'villages'],
  extraMarkers = null,
  styleUrl = DEFAULT_STYLE,
  className = '',
  rememberViewKey = null,
  onResultsChange = null,
  onReady = null,
  onLocate = null,
  onRegionCategory = null,
  onRegionOpen = null,
}, ref) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isLV = language === 'lv';
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const companiesRef = useRef([]);
  const eventsRef = useRef([]);
  const regionsRef = useRef([]);
  // Phase 2 — lazy, per-viewport loading of the heavy village layer.
  const placeCountsRef = useRef({});
  const citiesRef = useRef([]);
  const villageIdsRef = useRef(new Set());
  const villagesFCRef = useRef([]);
  const villageBoxesRef = useRef([]);
  const villageTimerRef = useRef(null);
  const placePopupRef = useRef(null);
  const regionMarkersRef = useRef([]);
  const cityMarkersRef = useRef([]);
  const districtsRef = useRef([]);
  const districtMarkersRef = useRef([]);
  const declutterRafRef = useRef(0);
  const regionPopupRef = useRef(null);
  const regionPopupProtectRef = useRef(false);
  const investMarkersRef = useRef([]);
  const autoClsRef = useRef([]);          // all active classifieds geo (fetched once)
  const clsActiveRef = useRef(false);     // whether the classifieds dataset currently has points
  const clsOverrideRef = useRef(null);    // explorer-supplied classifieds (null → ambient auto)
  const extraClsRef = useRef(null);       // MapPage toggle-supplied classifieds (emoji extraMarkers)
  const exploreColorRef = useRef('#D97736'); // current category colour for explore clusters
  const applyClsDataRef = useRef(null);   // points at applyClsData (for imperative setClassifieds)
  const spiderRef = useRef([]);           // temporary spiderfy "leg" markers
  const hoverTipRef = useRef(null);       // hover name tooltip popup
  const investPopupRef = useRef(null);
  const investProgressRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const previousLayersRef = useRef(null);
  const onResultsChangeRef = useRef(onResultsChange);
  useEffect(() => { onResultsChangeRef.current = onResultsChange; }, [onResultsChange]);
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  const onLocateRef = useRef(onLocate);
  useEffect(() => { onLocateRef.current = onLocate; }, [onLocate]);
  const onRegionCategoryRef = useRef(onRegionCategory);
  useEffect(() => { onRegionCategoryRef.current = onRegionCategory; }, [onRegionCategory]);
  const onRegionOpenRef = useRef(onRegionOpen);
  useEffect(() => { onRegionOpenRef.current = onRegionOpen; }, [onRegionOpen]);
  // Admin-editable category marker colours (shared with the chips below the map).
  const catColorsRef = useRef({ shop: '#E11D48', service: '#7C3AED', event: '#DB2777', project: '#0891B2', business: '#CA8A04', classified: '#2F7E79' });
  const [catColorsVersion, setCatColorsVersion] = useState(0);
  useEffect(() => {
    axios.get(`${API}/map/colors`).then(({ data }) => {
      if (data?.colors) { catColorsRef.current = { ...catColorsRef.current, ...data.colors }; setCatColorsVersion((v) => v + 1); }
    }).catch(() => {});
  }, []);
  const [ready, setReady] = useState(false);
  // Imperative API for the instant-start wrapper (fly-to / locate).
  useImperativeHandle(ref, () => ({
    flyTo: ({ center, zoom, duration } = {}) => {
      const map = mapRef.current;
      if (!map || !center) return;
      if (regionPopupRef.current) { regionPopupRef.current.remove(); regionPopupRef.current = null; }
      map.flyTo({ center, zoom: zoom ?? 11, duration: duration ?? 1400, essential: true });
    },
    locate: () => locateMe(),
    isReady: () => !!mapRef.current && ready,
    getMap: () => mapRef.current,
    // --- Category Explorer (below-map chips) control the map via these ---
    showExplore: (items, color) => {
      const map = mapRef.current;
      if (!map || !map.getSource('explore')) return;
      exploreColorRef.current = color || '#D97736';
      if (map.getLayer('explore-clusters')) map.setPaintProperty('explore-clusters', 'circle-color', exploreColorRef.current);
      const feats = (items || [])
        .filter((it) => it.lat != null && it.lng != null)
        .map((it) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [it.lng, it.lat] },
          properties: { name: it.name || '', link: it.link || '', color: color || '#D97736' },
        }));
      map.getSource('explore').setData({ type: 'FeatureCollection', features: feats });
    },
    clearExplore: () => {
      const map = mapRef.current;
      if (map && map.getSource('explore')) map.getSource('explore').setData({ type: 'FeatureCollection', features: [] });
    },
    // Classifieds are rendered as the dedicated 🧳 clustered markers (not the
    // generic explore circles). Pass an array to show that filtered set, or
    // null to fall back to the ambient auto-fetched classifieds.
    setClassifieds: (items) => { clsOverrideRef.current = Array.isArray(items) ? items : null; applyClsDataRef.current?.(); },
    fitExplore: (items) => {
      const map = mapRef.current;
      const pts = (items || []).filter((it) => it.lat != null && it.lng != null);
      if (!map || !pts.length) return;
      if (pts.length === 1) { map.flyTo({ center: [pts[0].lng, pts[0].lat], zoom: 12, duration: 1000 }); return; }
      let w = 180, s = 90, e = -180, n = -90;
      pts.forEach((p) => { w = Math.min(w, p.lng); e = Math.max(e, p.lng); s = Math.min(s, p.lat); n = Math.max(n, p.lat); });
      map.fitBounds([[w, s], [e, n]], { padding: 70, maxZoom: 13, duration: 1000 });
    },
    getBounds: () => {
      const b = mapRef.current?.getBounds();
      return b ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] : null;
    },
  }), [ready]);
  const popupRef = useRef(null);
  const [loadingData, setLoadingData] = useState(false);
  const [enabled, setEnabled] = useState(() =>
    Object.fromEntries(FILTERS.map((f) => [f.key, enabledLayers.includes(f.key) && !DEFAULT_OFF.includes(f.key)]))
  );
  const enabledRef = useRef(null);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // --- Declutter: when composite markers (region / city / district) overlap on
  // screen, keep the MOST important one (highest activity count) and hide the
  // rest so dense areas stay readable. Runs (throttled via rAF) on every pan /
  // zoom and after each marker render. Only band-visible markers participate. ---
  const declutterMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const items = [];
    const collect = (ref, level) => {
      (ref.current || []).forEach((m) => {
        const el = m.getElement();
        if (!el || el.classList.contains('kolv-hidden')) return;
        const pt = map.project(m.getLngLat());
        items.push({
          el, x: pt.x, y: pt.y, level,
          count: parseFloat(el.dataset.count || '0') || 0,
          r: parseFloat(el.dataset.r || '20') || 20,
        });
      });
    };
    collect(regionMarkersRef, 0);
    collect(cityMarkersRef, 1);
    collect(districtMarkersRef, 2);
    // Highest count wins; declutter is WITHIN a level only (region↔region,
    // city↔city, district↔district) so a city's summary bubble never hides its
    // own neighbourhood districts — it just sits on top of them (higher z-index).
    items.sort((a, b) => (b.count - a.count) || (a.level - b.level));
    const placedByLevel = { 0: [], 1: [], 2: [] };
    items.forEach((it) => {
      const placed = placedByLevel[it.level] || [];
      let hide = false;
      for (let i = 0; i < placed.length; i++) {
        const q = placed[i];
        const dx = it.x - q.x, dy = it.y - q.y;
        const minD = (it.r + q.r) * 0.85;
        if (dx * dx + dy * dy < minD * minD) { hide = true; break; }
      }
      if (hide) it.el.classList.add('kolv-declutter-hidden');
      else { it.el.classList.remove('kolv-declutter-hidden'); placed.push(it); }
    });
  }, []);
  const scheduleDeclutter = useCallback(() => {
    if (declutterRafRef.current) return;
    declutterRafRef.current = requestAnimationFrame(() => {
      declutterRafRef.current = 0;
      declutterMarkers();
    });
  }, [declutterMarkers]);
  const scheduleDeclutterRef = useRef(scheduleDeclutter);
  useEffect(() => { scheduleDeclutterRef.current = scheduleDeclutter; }, [scheduleDeclutter]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const searchTimer = useRef(null);
  const userMarkerRef = useRef(null);
  const pulseRafRef = useRef(null);
  const userLocRef = useRef(null);
  // Business ("Uzņēmumi") layer filter state.
  const [bizFilters, setBizFilters] = useState({ kind: 'all', activity: [], deal: [], openNow: false, delivery: false, topRated: false, freeSlots: false, near: 0 });
  const [bizPanelOpen, setBizPanelOpen] = useState(true);
  const [regionInvest, setRegionInvest] = useState(null); // {campaign, name} for map region sponsorship
  const [bizSheetOpen, setBizSheetOpen] = useState(false);
  const [bizCount, setBizCount] = useState(0);
  // Events ("Pasākumi") layer filter state.
  const [eventFilters, setEventFilters] = useState({ categories: [], price: 'all', date: 'all' });
  const [eventPanelOpen, setEventPanelOpen] = useState(true);
  const [eventSheetOpen, setEventSheetOpen] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const h = () => setIsMobile(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  const wantPlaces = PLACE_LAYERS.filter((p) => enabledLayers.includes(p.key));
  const wantCompanies = enabledLayers.includes('companies');
  const wantEvents = enabledLayers.includes('events');
  // Which geography band is active for the zoom legend highlight.
  const zoomLevelIndex = currentZoom < REGION_MAX_ZOOM ? 0 : currentZoom < 8.3 ? 1 : currentZoom < 9.5 ? 2 : 3;

  const coverageText = (saType, radius) => {
    if (saType === 'country') return (language === 'lv' ? 'Pieejams visā Latvijā' : language === 'ru' ? 'Доступно по всей Латвии' : 'Available across Latvia');
    if (saType === 'radius') return language === 'lv' ? `Apkalpo ${radius ? `~${radius} km` : 'noteiktā'} rādiusā` : language === 'ru' ? `Обслуживает радиус ${radius ? `~${radius} км` : ''}`.trim() : `Serves ${radius ? `~${radius} km` : 'a'} radius`;
    if (saType === 'region') return (language === 'lv' ? 'Apkalpo reģionu' : language === 'ru' ? 'Обслуживает регион' : 'Serves the region');
    if (saType === 'cities') return (language === 'lv' ? 'Apkalpo izvēlētās pilsētas' : language === 'ru' ? 'Обслуживает выбранные города' : 'Serves selected cities');
    return '';
  };

  // ---- Map init (once) ----
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;
    // Restore a previously-saved view (center+zoom) so navigating to a city /
    // listing and pressing Back returns the user to where they left off.
    let savedView = null;
    if (rememberViewKey) {
      try {
        const raw = sessionStorage.getItem(rememberViewKey);
        if (raw) {
          const v = JSON.parse(raw);
          if (v && Array.isArray(v.center) && typeof v.zoom === 'number') savedView = v;
        }
      } catch (_) { /* noop */ }
    }
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: savedView?.center || initialCenter,
      zoom: savedView?.zoom ?? initialZoom,
      attributionControl: false,
      preserveDrawingBuffer: true,
      // Keep the user over the Baltics: can't zoom out into the ocean/world
      // (minZoom), can't over-zoom past tile detail (maxZoom), no repeated world
      // copies, and panning is clamped to a box around LV (+ EE/LT invest overlays).
      minZoom: 5.5,
      maxZoom: 17,
      renderWorldCopies: false,
      maxBounds: [[18.0, 54.5], [30.5, 59.5]],
    });
    mapRef.current = map;
    if (typeof window !== 'undefined') window.__kolvMap = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.on('error', (e) => { console.warn('[VectorMap]', e?.error?.message || e?.error || e); });

    // Persist the view on every move so Back restores it.
    if (rememberViewKey) {
      map.on('moveend', () => {
        try {
          sessionStorage.setItem(rememberViewKey, JSON.stringify({
            center: map.getCenter().toArray(), zoom: map.getZoom(),
          }));
        } catch (_) { /* noop */ }
      });
    }

    // Track zoom so the legend + HTML markers (regions, invest CTAs) can react
    // to the progressive-disclosure bands. Marker visibility toggles on every
    // tick (cheap DOM), React state updates only when the band actually changes.
    const bandOf = (v) => (v < REGION_MAX_ZOOM ? 0 : v < 8.3 ? 1 : v < 9.5 ? 2 : 3);
    map.on('zoom', () => {
      const z = map.getZoom();
      const show = z < REGION_MAX_ZOOM;
      regionMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
      investMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
      const showCity = (enabledRef.current?.cities ?? true) && z >= REGION_MAX_ZOOM;
      cityMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !showCity));
      const showDist = (enabledRef.current?.districts ?? true) && z >= DISTRICT_COMPOSITE_MIN;
      districtMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !showDist));
      // A freshly-opened region card must survive the click's own zoom-IN
      // animation (which starts in the region band). Once we've zoomed past the
      // band, stop protecting it; then close it when the user zooms back OUT.
      if (!show) regionPopupProtectRef.current = false;
      if (show && regionPopupRef.current && !regionPopupProtectRef.current) { regionPopupRef.current.remove(); regionPopupRef.current = null; }
      setCurrentZoom((prev) => (bandOf(prev) === bandOf(z) ? prev : z));
      scheduleDeclutterRef.current?.();
    });
    map.on('move', () => { scheduleDeclutterRef.current?.(); });

    const setPointer = (layer) => {
      map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
    };

    map.on('load', () => {
      if (fitLatvia && !savedView) map.fitBounds(LATVIA_BOUNDS, { padding: 30, duration: 0 });

      // ---- Overlap helpers: hover name tooltips + cluster spiderfy ----
      // Names are hidden when zoomed out and shown on HOVER (any zoom) / click /
      // close zoom, so dense areas stop drowning in overlapping labels.
      const addHoverTip = (layer) => {
        map.on('mouseenter', layer, (e) => {
          const p = e.features?.[0]?.properties; if (!p || !p.name) return;
          map.getCanvas().style.cursor = 'pointer';
          if (hoverTipRef.current) hoverTipRef.current.remove();
          hoverTipRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 14, className: 'kolv-hover-tip' })
            .setLngLat(e.features[0].geometry.coordinates)
            .setHTML(`<span>${String(p.name).replace(/</g, '&lt;')}</span>`).addTo(map);
        });
        map.on('mouseleave', layer, () => {
          map.getCanvas().style.cursor = '';
          if (hoverTipRef.current) { hoverTipRef.current.remove(); hoverTipRef.current = null; }
        });
      };
      const clearSpider = () => { spiderRef.current.forEach((m) => m.remove()); spiderRef.current = []; };
      // Stop a marker's pointer gesture from reaching the map canvas so clicking
      // a cluster/pin never also triggers a map zoom / place-drill (which would
      // override spiderfy). Covers mouse + touch + synthetic clicks.
      const swallow = (el) => ['mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart', 'touchend', 'click', 'dblclick', 'contextmenu'].forEach((t) => el.addEventListener(t, (ev) => ev.stopPropagation()));
      // Fan a cluster's leaves out around its centre (pixel offsets) so markers
      // that can't separate by zoom stop stacking on top of each other.
      const spiderfy = (center, leaves, buildEl) => {
        clearSpider();
        const n = leaves.length;
        if (!n) return;
        const R = n <= 8 ? 56 : 56 + (n - 8) * 4.5;
        leaves.forEach((leaf, i) => {
          const ang = (2 * Math.PI * i) / n - Math.PI / 2;
          const el = buildEl(leaf);
          el.classList.add('kolv-spider-leg');
          const marker = new maplibregl.Marker({ element: el, offset: [Math.cos(ang) * R, Math.sin(ang) * R], anchor: 'center' })
            .setLngLat(center).addTo(map);
          spiderRef.current.push(marker);
        });
      };
      map.on('zoomstart', clearSpider);
      const openCompanyPopup = (coordinates, p) => {
        if (!p) return;
        const activityLabel = { eat: '🍽️ Paēst & Izbaudīt', entertainment: '🎉 Izklaide', shop: '🛍️ Iepirkties', service: '🔧 Pakalpojumi' }[p.primary] || '';
        const rating = (p.rating && p.rating !== 'null') ? `<span style="color:#F59E0B;font-weight:700">★ ${p.rating}</span>` : '';
        const openBadge = p.open_now === true || p.open_now === 'true'
          ? `<span style="color:#16A34A;font-weight:600;font-size:11px">● ${(language === 'lv' ? 'Atvērts tagad' : language === 'ru' ? 'Открыто сейчас' : 'Open now')}</span>`
          : `<span style="color:#9CA3AF;font-size:11px">● ${(language === 'lv' ? 'Slēgts' : language === 'ru' ? 'Закрыто' : 'Closed')}</span>`;
        const isBizPage = (p.template && p.template !== 'null' && p.template !== '');
        const dest = isBizPage ? (p.page_id ? `/uznemums/${p.id}/${p.page_id}` : `/uznemums/${p.id}`) : `/shop/${p.id}`;
        const cta = isBizPage ? ((language === 'lv' ? 'Skatīt biznesa lapu' : language === 'ru' ? 'Открыть страницу бизнеса' : 'View business page')) : ((language === 'lv' ? 'Skatīt veikalu' : language === 'ru' ? 'Открыть магазин' : 'View shop'));
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new maplibregl.Popup({ offset: 14, closeButton: true, maxWidth: '260px' })
          .setLngLat(coordinates)
          .setHTML(
            `<div style="font-family:Outfit,sans-serif">
               <div style="font-weight:600;color:#121212;font-size:14px;margin-bottom:3px">${p.name || ''}${p.verified === true || p.verified === 'true' ? ' <span style=\"color:#2563EB\">✔</span>' : ''}</div>
               <div style="font-size:11px;color:#5C5C5C;margin-bottom:4px">${activityLabel}</div>
               <div style="display:flex;gap:10px;align-items:center;margin-bottom:8px">${openBadge} ${rating}</div>
               <button data-goto="${dest}" style="width:100%;background:#7C3AED;color:#fff;border:none;border-radius:8px;padding:7px 10px;font-size:12px;font-weight:600;cursor:pointer">${cta}</button>
             </div>`
          )
          .addTo(map);
        const btn = popupRef.current.getElement()?.querySelector('[data-goto]');
        if (btn) btn.addEventListener('click', () => { popupRef.current?.remove(); navigate(btn.getAttribute('data-goto')); });
      };

      // --- Investment country overlays (Estonia / Lithuania) ---
      // The whole country is highlighted in its own borders as "open for
      // investment"; clicking it opens the invest modal. Fades out on zoom-in.
      if (showFilters) {
        const investFeatures = Object.entries(COUNTRY_SHAPES).map(([slug, geom]) => ({
          type: 'Feature',
          properties: {
            slug, campaign: slug === 'estonia' ? 'region_ee' : 'region_lt',
            name: slug === 'estonia' ? ((language === 'lv' ? 'Igaunija' : language === 'ru' ? 'Эстония' : 'Estonia')) : ((language === 'lv' ? 'Lietuva' : language === 'ru' ? 'Литва' : 'Lithuania')),
          },
          geometry: geom,
        }));
        map.addSource('invest-countries', { type: 'geojson', data: { type: 'FeatureCollection', features: investFeatures } });
        const investFade = ['interpolate', ['linear'], ['zoom'], 5.5, 1, 7.2, 0.75, 8.4, 0];
        // fill-opacity must be a top-level zoom interpolate (not nested in '*'),
        // so bake the 0.2 factor straight into the interpolate output stops.
        const investFillFade = ['interpolate', ['linear'], ['zoom'], 5.5, 0.2, 7.2, 0.15, 8.4, 0];
        map.addLayer({
          id: 'invest-fill', type: 'fill', source: 'invest-countries',
          paint: { 'fill-color': '#D97736', 'fill-opacity': investFillFade },
        });
        map.addLayer({
          id: 'invest-line', type: 'line', source: 'invest-countries',
          paint: { 'line-color': '#D97736', 'line-width': 2, 'line-dasharray': [2, 1.4], 'line-opacity': investFade },
        });
        map.on('click', 'invest-fill', (e) => {
          const p = e.features?.[0]?.properties;
          if (p?.campaign) setRegionInvest({ campaign: p.campaign, name: p.name });
        });
        setPointer('invest-fill');
      }

      // --- Browse place bubbles ---
      // Every place (city, district, village) is a coloured BUBBLE whose size
      // grows with its activity count and shows that NUMBER centred inside it.
      // Clicking a bubble DRILLS DOWN: the map zooms into it to reveal the next,
      // finer level (region → cities → districts → villages).
      const BUBBLE_R = (min) => ['interpolate', ['linear'], ['coalesce', ['get', 'count'], 0],
        0, min, 1, min + 4, 10, min + 9, 50, min + 15, 200, min + 22];
      const MIN_R = { cities: 10, districts: 9, villages: 8 };
      wantPlaces.forEach((cfg) => {
        map.addSource(cfg.key, { type: 'geojson', data: EMPTY_FC });
        const isCity = cfg.key === 'cities';
        // Places with activity use the layer colour; empty (count 0) places are
        // a smaller NEUTRAL grey dot so they don't compete for attention.
        const minR = MIN_R[cfg.key] || 8;
        const neutralR = Math.max(3.5, minR - 5);
        const colorExpr = ['case', ['>', ['coalesce', ['get', 'count'], 0], 0], cfg.color, '#BBB6AC'];
        const radiusExpr = ['case', ['==', ['coalesce', ['get', 'count'], 0], 0], neutralR, BUBBLE_R(minR)];
        map.addLayer({
          id: `${cfg.key}-point`, type: 'circle', source: cfg.key,
          layout: { 'circle-sort-key': ['coalesce', ['get', 'count'], 0] },
          paint: {
            'circle-color': colorExpr, 'circle-opacity': cfg.opacityExpr,
            'circle-radius': radiusExpr,
            'circle-stroke-color': '#ffffff', 'circle-stroke-width': isCity ? 1.5 : 1.1,
            'circle-stroke-opacity': cfg.opacityExpr,
          },
        });
        // NUMBER centred inside the bubble (only where the place has activity).
        map.addLayer({
          id: `${cfg.key}-count`, type: 'symbol', source: cfg.key,
          layout: {
            'text-field': ['case', ['>', ['coalesce', ['get', 'count'], 0], 0], ['to-string', ['get', 'count']], ''],
            'text-font': ['Noto Sans Bold'], 'text-size': isCity ? 12 : 11, 'text-allow-overlap': true,
          },
          paint: { 'text-color': '#ffffff', 'text-opacity': cfg.opacityExpr },
        });
        // Name below the bubble.
        map.addLayer({
          id: `${cfg.key}-label`, type: 'symbol', source: cfg.key, minzoom: cfg.labelMinZoom,
          layout: {
            'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': 11,
            'text-offset': [0, 1.5], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-optional': true,
            // Bigger (more active) places win the name-collision so their name shows first.
            'symbol-sort-key': ['-', 0, ['coalesce', ['get', 'count'], 0]],
          },
          paint: { 'text-color': cfg.color, 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
        });
        setPointer(`${cfg.key}-point`);
        // Districts with activity are drawn as composite HTML markers (like
        // cities), so the native circle/number/label render ONLY for empty
        // (count 0) districts — small grey context dots with no duplicate.
        if (cfg.key === 'districts') {
          const neutralOnly = ['==', ['coalesce', ['get', 'count'], 0], 0];
          map.setFilter('districts-point', neutralOnly);
          map.setFilter('districts-count', neutralOnly);
          map.setFilter('districts-label', neutralOnly);
        }
      });

      // Small popup offering to open a place's page (one extra tap — a bubble
      // click itself only drills down / zooms in).
      const showPlacePopup = (coords, name, slug) => {
        if (placePopupRef.current) placePopupRef.current.remove();
        const openTxt = language === 'lv' ? `Atvērt ${name || ''}` : language === 'ru' ? `Открыть ${name || ''}` : `Open ${name || ''}`;
        const html =
          `<div style="text-align:center;padding:2px;">` +
            `<div style="font-weight:600;font-size:13px;color:#121212;margin-bottom:6px;">${name || ''}</div>` +
            `<button type="button" class="kolv-place-pop-btn" style="background:#D97736;color:#fff;border:none;border-radius:999px;padding:5px 13px;font-size:12px;font-weight:600;cursor:pointer;">${openTxt} →</button>` +
          `</div>`;
        const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 16, className: 'kolv-place-popup', maxWidth: '210px' })
          .setLngLat(coords).setHTML(html).addTo(map);
        popup.getElement().querySelector('.kolv-place-pop-btn')?.addEventListener('click', () => { popup.remove(); navigate(`/region/${slug}`); });
        placePopupRef.current = popup;
      };

      // ONE unified click handler for all place bubbles → clicking never triggers
      // two things at once. It drills down (zooms into the clicked place) and
      // offers to open its page. Priority when bubbles overlap: city → district
      // → village (matches what is visually on top).
      const DRILL_ZOOM = { city: 9.0, district: 11.0, village: 13.0 };
      const DRILL_ORDER = { 'cities-point': 0, 'districts-point': 1, 'villages-point': 2 };
      map.on('click', (e) => {
        clearSpider();
        const layers = wantPlaces.map((c) => `${c.key}-point`).filter((id) => map.getLayer(id));
        if (!layers.length) return;
        const feats = map.queryRenderedFeatures(e.point, { layers });
        if (!feats.length) return;
        feats.sort((a, b) => (DRILL_ORDER[a.layer.id] ?? 9) - (DRILL_ORDER[b.layer.id] ?? 9));
        const f = feats[0];
        const p = f.properties || {};
        if (!p.slug) return;
        const coords = f.geometry.coordinates.slice();
        const target = DRILL_ZOOM[p.loc_type] || 12;
        map.easeTo({ center: coords, zoom: Math.max(map.getZoom() + 0.6, target), duration: 800 });
        showPlacePopup(coords, p.name, p.slug);
      });

      // --- Companies / listings pins (clustered when close together) ---
      if (wantCompanies) {
        map.addSource('companies', {
          type: 'geojson', data: EMPTY_FC,
          cluster: true, clusterRadius: 50, clusterMaxZoom: 13,
        });
        // Merged cluster bubble + count (shows when businesses are too close).
        map.addLayer({
          id: 'companies-clusters', type: 'circle', source: 'companies', filter: ['has', 'point_count'],
          paint: {
            'circle-color': COMPANY_COLOR, 'circle-opacity': 0.9,
            'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3,
            'circle-radius': ['step', ['get', 'point_count'], 16, 5, 22, 15, 30, 50, 38],
          },
        });
        map.addLayer({
          id: 'companies-cluster-count', type: 'symbol', source: 'companies', filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 13 },
          paint: { 'text-color': '#ffffff' },
        });
        map.addLayer({
          id: 'companies-pulse', type: 'circle', source: 'companies', filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': ['coalesce', ['get', 'color'], COMPANY_COLOR],
            'circle-opacity': 0.4, 'circle-radius': 9,
          },
        });
        map.addLayer({
          id: 'companies-point', type: 'circle', source: 'companies', filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': ['coalesce', ['get', 'color'], COMPANY_COLOR], 'circle-opacity': 0.95,
            'circle-radius': ['case', ['==', ['get', 'verified'], true], 9, 7],
            'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2,
          },
        });
        map.addLayer({
          id: 'companies-label', type: 'symbol', source: 'companies', minzoom: 13, filter: ['!', ['has', 'point_count']],
          layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': 12, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
          paint: { 'text-color': '#121212', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 },
        });
        // Click a merged bubble → zoom in until the businesses separate; when
        // they can't separate any further (same spot / max zoom), spiderfy them.
        map.on('click', 'companies-clusters', (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const src = map.getSource('companies');
          const cid = f.properties.cluster_id;
          Promise.all([
            Promise.resolve(src.getClusterExpansionZoom(cid)).catch(() => null),
            src.getClusterLeaves(cid, 100, 0).catch(() => []),
          ]).then(([zoom, leaves]) => {
            const lats = leaves.map((l) => l.geometry.coordinates[1]);
            const lngs = leaves.map((l) => l.geometry.coordinates[0]);
            const spread = Math.max((Math.max(...lats) - Math.min(...lats)) || 0, (Math.max(...lngs) - Math.min(...lngs)) || 0);
            if (leaves.length && (spread < 0.0009 || map.getZoom() >= 15.5)) {
              spiderfy(f.geometry.coordinates, leaves, (leaf) => {
                const lp = leaf.properties || {};
                const el = document.createElement('button');
                el.type = 'button';
                el.className = 'kolv-company-leg';
                el.setAttribute('data-testid', 'company-spider-leg');
                el.title = lp.name || '';
                el.style.background = (lp.color && lp.color !== 'null') ? lp.color : COMPANY_COLOR;
                swallow(el);
                el.addEventListener('click', (ev) => { ev.stopPropagation(); openCompanyPopup(leaf.geometry.coordinates, lp); });
                return el;
              });
            } else {
              map.easeTo({ center: f.geometry.coordinates, zoom: Math.min((zoom || map.getZoom()) + 0.6, 16) });
            }
          }).catch(() => map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(map.getZoom() + 1.5, 16) }));
        });
        setPointer('companies-clusters');
        map.on('click', 'companies-point', (e) => {
          openCompanyPopup(e.features[0].geometry.coordinates, e.features?.[0]?.properties);
        });
        addHoverTip('companies-point');
      }

      // --- Events pins (admin-published public events) ---
      if (wantEvents) {
        // Clustered source: zoomed out shows aggregated counts (city level),
        // zooming in splits them (district) until individual precise pins appear.
        map.addSource('events', {
          type: 'geojson', data: EMPTY_FC,
          cluster: true, clusterMaxZoom: 12, clusterRadius: 55,
        });
        // Aggregated cluster bubbles with count.
        map.addLayer({
          id: 'events-clusters', type: 'circle', source: 'events',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': EVENTS_COLOR,
            'circle-opacity': 0.9,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 3,
            'circle-radius': ['step', ['get', 'point_count'], 18, 5, 24, 15, 32],
          },
        });
        map.addLayer({
          id: 'events-cluster-count', type: 'symbol', source: 'events',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Noto Sans Regular'], 'text-size': 14,
          },
          paint: { 'text-color': '#ffffff' },
        });
        map.addLayer({
          id: 'events-pulse', type: 'circle', source: 'events',
          filter: ['!', ['has', 'point_count']],
          paint: { 'circle-color': EVENTS_COLOR, 'circle-opacity': 0.4, 'circle-radius': 10 },
        });
        map.addLayer({
          id: 'events-point', type: 'circle', source: 'events',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': EVENTS_COLOR, 'circle-opacity': 0.95, 'circle-radius': 8,
            'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2,
          },
        });
        map.addLayer({
          id: 'events-label', type: 'symbol', source: 'events', minzoom: 13,
          filter: ['!', ['has', 'point_count']],
          layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': 12, 'text-offset': [0, 1.3], 'text-anchor': 'top' },
          paint: { 'text-color': EVENTS_COLOR, 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 },
        });
        // Build a chronological list popup for several events sharing a venue.
        const buildEventListHTML = (list) => {
          const sorted = [...list].sort((a, b) => {
            const da = a.event_date && a.event_date !== 'null' ? new Date(a.event_date).getTime() : Infinity;
            const dbb = b.event_date && b.event_date !== 'null' ? new Date(b.event_date).getTime() : Infinity;
            return da - dbb;
          });
          const rows = sorted.map((p) => {
            const when = p.event_date && p.event_date !== 'null'
              ? new Date(p.event_date).toLocaleDateString('lv-LV', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
              : '';
            const price = (p.is_paid === true || p.is_paid === 'true')
              ? `<span style="color:#E11D48;font-weight:700">€${p.price && p.price !== 'null' ? p.price : ''}</span>`
              : `<span style="color:#16A34A;font-weight:700">${(language === 'lv' ? 'Bezmaksas' : language === 'ru' ? 'Бесплатно' : 'Free')}</span>`;
            return `<button data-goto="/events/${p.id}" style="display:block;width:100%;text-align:left;background:#fff;border:0;border-bottom:1px solid #F0EEEA;padding:9px 10px;cursor:pointer">
              <div style="font-weight:600;color:#121212;font-size:13px;line-height:1.2;margin-bottom:2px">${p.name || ''}</div>
              <div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;color:#5C5C5C"><span>🕑 ${when}</span>${price}</div>
            </button>`;
          }).join('');
          const venue = sorted[0]?.location_name || '';
          return `<div style="font-family:Outfit,sans-serif;width:240px">
            <div style="padding:9px 10px;background:${EVENTS_COLOR};color:#fff;font-weight:700;font-size:13px">📍 ${venue} · ${sorted.length} ${(language === 'lv' ? 'pasākumi' : language === 'ru' ? 'мероприятий' : 'events')}</div>
            <div style="max-height:230px;overflow-y:auto">${rows}</div>
          </div>`;
        };
        const showEventList = (lngLat, list) => {
          if (popupRef.current) popupRef.current.remove();
          popupRef.current = new maplibregl.Popup({ offset: 14, closeButton: true, maxWidth: '260px', className: 'kolv-event-list-popup' })
            .setLngLat(lngLat).setHTML(buildEventListHTML(list)).addTo(map);
          popupRef.current.getElement().querySelectorAll('[data-goto]').forEach((b) =>
            b.addEventListener('click', () => { const l = b.getAttribute('data-goto'); popupRef.current?.remove(); navigate(l); }));
        };

        // Click a cluster: if its events share one venue, list them by date;
        // otherwise zoom in to split it (city > district > pin).
        map.on('click', 'events-clusters', (e) => {
          const feat = e.features?.[0];
          if (!feat) return;
          const src = map.getSource('events');
          const cid = feat.properties.cluster_id;
          Promise.all([
            Promise.resolve(src.getClusterExpansionZoom(cid)).catch(() => null),
            src.getClusterLeaves(cid, 200, 0),
          ]).then(([zoom, leaves]) => {
            const lats = leaves.map((l) => l.geometry.coordinates[1]);
            const lngs = leaves.map((l) => l.geometry.coordinates[0]);
            const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
            if (spread < 0.0008) {
              showEventList(feat.geometry.coordinates, leaves.map((l) => l.properties));
            } else {
              map.easeTo({ center: feat.geometry.coordinates, zoom: Math.min((zoom || map.getZoom() + 1.5) + 0.5, 15) });
            }
          }).catch(() => map.easeTo({ center: feat.geometry.coordinates, zoom: Math.min(map.getZoom() + 1.5, 15) }));
        });
        setPointer('events-clusters');
        map.on('click', 'events-point', (e) => {
          const p = e.features?.[0]?.properties;
          if (!p) return;
          const c = e.features[0].geometry.coordinates;
          // Several events at the exact same spot → chronological list popup.
          const near = (eventsRef.current || []).filter((ft) =>
            Math.abs(ft.geometry.coordinates[0] - c[0]) < 0.0008 &&
            Math.abs(ft.geometry.coordinates[1] - c[1]) < 0.0008);
          if (near.length > 1) { showEventList(c, near.map((f) => f.properties)); return; }
          const when = p.event_date && p.event_date !== 'null'
            ? new Date(p.event_date).toLocaleDateString('lv-LV', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
            : '';
          const priceBadge = (p.is_paid === true || p.is_paid === 'true')
            ? `<span style="color:#E11D48;font-weight:700;font-size:11px">€ ${p.price && p.price !== 'null' ? p.price : ''}</span>`
            : `<span style="color:#16A34A;font-weight:700;font-size:11px">${(language === 'lv' ? 'Bezmaksas' : language === 'ru' ? 'Бесплатно' : 'Free')}</span>`;
          const img = (p.image && p.image !== 'null') ? `<img src="${p.image}" style="width:100%;height:90px;object-fit:cover;border-radius:8px;margin-bottom:6px" />` : '';
          if (popupRef.current) popupRef.current.remove();
          popupRef.current = new maplibregl.Popup({ offset: 14, closeButton: true, maxWidth: '260px' })
            .setLngLat(e.features[0].geometry.coordinates)
            .setHTML(
              `<div style="font-family:Outfit,sans-serif">
                 ${img}
                 <div style="font-weight:600;color:#121212;font-size:14px;margin-bottom:2px">${p.name || ''}</div>
                 <div style="font-size:11px;color:#5C5C5C;margin-bottom:2px">📍 ${p.location_name || ''}</div>
                 <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px"><span style="font-size:11px;color:#5C5C5C">${when}</span> ${priceBadge}</div>
                 <button data-goto="/events/${p.id}" style="width:100%;background:${EVENTS_COLOR};color:#fff;border:none;border-radius:8px;padding:7px 10px;font-size:12px;font-weight:600;cursor:pointer">${(language === 'lv' ? 'Skatīt pasākumu' : language === 'ru' ? 'Открыть мероприятие' : 'View event')}</button>
               </div>`
            )
            .addTo(map);
          const btn = popupRef.current.getElement()?.querySelector('[data-goto]');
          if (btn) btn.addEventListener('click', () => { popupRef.current?.remove(); navigate(btn.getAttribute('data-goto')); });
        });
        addHoverTip('events-point');
      }

      // --- Classifieds 🧳 (clustered HTML markers, like companies/events) ---
      // Overlapping suitcases in one town collapse into ONE teal 🧳 bubble with
      // a count that splits as you zoom; at max zoom a cluster spiderfies.
      map.addSource('cls', { type: 'geojson', data: EMPTY_FC, cluster: true, clusterRadius: 60, clusterMaxZoom: 14 });
      // Hidden point layer so the clustered source gets tiled → querySourceFeatures works.
      map.addLayer({ id: 'cls-src', type: 'circle', source: 'cls', paint: { 'circle-radius': 1, 'circle-opacity': 0, 'circle-color': '#2F7E79' } });
      const CLS_MIN_ZOOM = 9.5;   // below this, region/city 🧳 satellites show the aggregate
      const buildClsPin = (p) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'kolv-cls-pin';
        el.setAttribute('data-testid', 'classified-map-pin');
        el.title = p.title || '';
        el.innerHTML = `<span>${p.emoji || '🧳'}</span>`;
        swallow(el);
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const link = p.link || ((p.slug || p.id) ? `/sludinajums/${p.slug || p.id}` : '');
          if (link) navigate(link);
        });
        return el;
      };
      const buildClsCluster = (count) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'kolv-cls-cluster';
        el.setAttribute('data-testid', 'cls-cluster');
        el.innerHTML = `<span class="kolv-cls-emoji">🧳</span><span class="kolv-cls-num">${count}</span>`;
        swallow(el);
        return el;
      };
      const onClsClusterClick = (f) => {
        const src = map.getSource('cls');
        const cid = f.properties.cluster_id;
        Promise.all([
          Promise.resolve(src.getClusterExpansionZoom(cid)).catch(() => null),
          src.getClusterLeaves(cid, 100, 0).catch(() => []),
        ]).then(([zoom, leaves]) => {
          const lats = leaves.map((l) => l.geometry.coordinates[1]);
          const lngs = leaves.map((l) => l.geometry.coordinates[0]);
          const spread = Math.max((Math.max(...lats) - Math.min(...lats)) || 0, (Math.max(...lngs) - Math.min(...lngs)) || 0);
          if (leaves.length && (spread < 0.0009 || map.getZoom() >= 15.5)) {
            spiderfy(f.geometry.coordinates, leaves, (leaf) => buildClsPin(leaf.properties || {}));
          } else {
            map.easeTo({ center: f.geometry.coordinates, zoom: Math.min((zoom || map.getZoom()) + 0.8, 16.5) });
          }
        }).catch(() => map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(map.getZoom() + 1.5, 16.5) }));
      };
      let clsOnScreen = [];
      const syncCls = () => {
        if (!map.getSource('cls') || !map.isSourceLoaded('cls')) return;
        clsOnScreen.forEach((m) => m.remove());
        clsOnScreen = [];
        // Ambient discovery is gated to >= 9.5 (region/city satellites cover
        // lower zooms); an explicit selection (explorer/toggle) shows at any zoom.
        const ambient = !Array.isArray(clsOverrideRef.current) && !Array.isArray(extraClsRef.current);
        if (!clsActiveRef.current || (ambient && map.getZoom() < CLS_MIN_ZOOM)) return;
        const feats = map.querySourceFeatures('cls');
        const seen = new Set();
        for (const f of feats) {
          const p = f.properties || {};
          const id = p.cluster ? `c${p.cluster_id}` : `p${p.slug || p.id || f.geometry.coordinates.join(',')}`;
          if (seen.has(id)) continue;
          seen.add(id);
          const el = p.cluster ? buildClsCluster(p.point_count_abbreviated) : buildClsPin(p);
          if (p.cluster) { const ff = f; el.addEventListener('click', (ev) => { ev.stopPropagation(); onClsClusterClick(ff); }); }
          const marker = new maplibregl.Marker({ element: el, anchor: p.cluster ? 'center' : 'bottom' })
            .setLngLat(f.geometry.coordinates).addTo(map);
          clsOnScreen.push(marker);
        }
      };
      map.on('moveend', syncCls);
      map.on('data', (e) => { if (e.sourceId === 'cls' && e.isSourceLoaded) syncCls(); });

      // --- Optional custom markers (RegionPage seller pins) ---
      // Emoji markers (classifieds 🧳) are rendered separately as HTML markers
      // by a reactive effect, so exclude them from this static circle layer.
      if (extraMarkers && extraMarkers.length) {
        const fc = {
          type: 'FeatureCollection',
          features: extraMarkers.filter((m) => m.lng != null && m.lat != null && !m.emoji).map((m) => ({
            type: 'Feature', geometry: { type: 'Point', coordinates: [m.lng, m.lat] },
            properties: { name: m.name || '', link: m.link || '' },
          })),
        };
        map.addSource('extra-markers', { type: 'geojson', data: fc });
        map.addLayer({ id: 'extra-markers-point', type: 'circle', source: 'extra-markers', paint: { 'circle-color': '#D97736', 'circle-radius': 8, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } });
        map.addLayer({ id: 'extra-markers-label', type: 'symbol', source: 'extra-markers', layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': 12, 'text-offset': [0, 1.2], 'text-anchor': 'top' }, paint: { 'text-color': '#D97736', 'text-halo-color': '#fff', 'text-halo-width': 1.5 } });
        map.on('click', 'extra-markers-point', (e) => { const link = e.features?.[0]?.properties?.link; if (link) navigate(link); });
        setPointer('extra-markers-point');
      }

      // --- Category Explorer results layer (driven by the below-map chips) ---
      // Clustered so overlapping shops/services/events/projects in one town
      // collapse into one count bubble; labels hide until close zoom (hover
      // reveals the name); a max-zoom cluster spiderfies instead of stacking.
      map.addSource('explore', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, cluster: true, clusterRadius: 48, clusterMaxZoom: 14 });
      map.addLayer({
        id: 'explore-clusters', type: 'circle', source: 'explore', filter: ['has', 'point_count'],
        paint: {
          'circle-color': exploreColorRef.current, 'circle-opacity': 0.92,
          'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3,
          'circle-radius': ['step', ['get', 'point_count'], 16, 5, 22, 15, 30, 50, 38],
        },
      });
      map.addLayer({
        id: 'explore-cluster-count', type: 'symbol', source: 'explore', filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 13 },
        paint: { 'text-color': '#ffffff' },
      });
      map.addLayer({
        id: 'explore-halo', type: 'circle', source: 'explore', filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ['get', 'color'], 'circle-radius': 17, 'circle-opacity': 0.16,
          'circle-stroke-color': ['get', 'color'], 'circle-stroke-width': 1, 'circle-stroke-opacity': 0.3,
        },
      });
      map.addLayer({
        id: 'explore-point', type: 'circle', source: 'explore', filter: ['!', ['has', 'point_count']],
        paint: { 'circle-color': ['get', 'color'], 'circle-radius': 8, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2.5 },
      });
      map.addLayer({
        id: 'explore-label', type: 'symbol', source: 'explore', minzoom: 13, filter: ['!', ['has', 'point_count']],
        layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': 11, 'text-offset': [0, 1.3], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-optional': true },
        paint: { 'text-color': '#121212', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });
      map.on('click', 'explore-clusters', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const src = map.getSource('explore');
        const cid = f.properties.cluster_id;
        Promise.all([
          Promise.resolve(src.getClusterExpansionZoom(cid)).catch(() => null),
          src.getClusterLeaves(cid, 100, 0).catch(() => []),
        ]).then(([zoom, leaves]) => {
          const lats = leaves.map((l) => l.geometry.coordinates[1]);
          const lngs = leaves.map((l) => l.geometry.coordinates[0]);
          const spread = Math.max((Math.max(...lats) - Math.min(...lats)) || 0, (Math.max(...lngs) - Math.min(...lngs)) || 0);
          if (leaves.length && (spread < 0.0008 || map.getZoom() >= 15.5)) {
            spiderfy(f.geometry.coordinates, leaves, (leaf) => {
              const lp = leaf.properties || {};
              const el = document.createElement('button');
              el.type = 'button';
              el.className = 'kolv-company-leg';
              el.setAttribute('data-testid', 'explore-spider-leg');
              el.title = lp.name || '';
              el.style.background = (lp.color && lp.color !== 'null') ? lp.color : exploreColorRef.current;
              swallow(el);
              el.addEventListener('click', (ev) => { ev.stopPropagation(); if (lp.link) navigate(lp.link); });
              return el;
            });
          } else {
            map.easeTo({ center: f.geometry.coordinates, zoom: Math.min((zoom || map.getZoom()) + 0.7, 16) });
          }
        }).catch(() => map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(map.getZoom() + 1.5, 16) }));
      });
      setPointer('explore-clusters');
      map.on('click', 'explore-point', (e) => { const link = e.features?.[0]?.properties?.link; if (link) navigate(link); });
      addHoverTip('explore-point');

      // Apply default-hidden layers (added but off by default).
      DEFAULT_OFF.forEach((key) => {
        (LAYER_GROUPS[key] || []).forEach((id) => {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
        });
      });

      // Keep the city bubbles on top of every other layer (districts, villages,
      // events, companies) so they stay the primary/foreground markers.
      ['cities-point', 'cities-count', 'cities-label'].forEach((id) => {
        if (map.getLayer(id)) map.moveLayer(id);
      });

      // Continuous "ping" pulse on company/event pins so they read as tappable.
      // Only repaints while a pulse layer is actually visible.
      const PULSE_IDS = ['companies-pulse', 'events-pulse'];
      const animatePulse = () => {
        pulseRafRef.current = requestAnimationFrame(animatePulse);
        const visible = PULSE_IDS.filter((id) => map.getLayer(id) && map.getLayoutProperty(id, 'visibility') !== 'none');
        if (!visible.length) return;
        const t = (performance.now() % 1600) / 1600;
        const radius = 8 + t * 15;
        const opacity = 0.45 * (1 - t);
        visible.forEach((id) => {
          map.setPaintProperty(id, 'circle-radius', radius);
          map.setPaintProperty(id, 'circle-opacity', opacity);
        });
      };
      animatePulse();

      setReady(true);
      setCurrentZoom(map.getZoom());
      if (onReadyRef.current) { try { onReadyRef.current(map); } catch (_) { /* noop */ } }
      map.resize();
      setTimeout(() => { try { map.resize(); } catch (_) { /* noop */ } }, 300);
    });

    return () => { if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Fetch data once map + layers exist ----
  useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    if (!wantPlaces.length && !wantCompanies && !wantEvents) return;
    setLoadingData(true);
    const tasks = [];

    const typeToLayer = {};
    wantPlaces.forEach((cfg) => cfg.types.forEach((t) => { typeToLayer[t] = cfg.key; }));
    const wantVillages = wantPlaces.some((p) => p.key === 'villages');

    // Villages (8000+) are the heavy layer — load them per VISIBLE BBOX only,
    // when zoomed in, merging + de-duping as the user pans. Cities (81) and
    // districts (589) are light enough to load once up front.
    const loadVillages = () => {
      if (!wantVillages) return;
      const z = map.getZoom();
      if (z < 8.8) return; // villages fade in ~9.0 — don't fetch earlier
      const b = map.getBounds();
      const dLng = (b.getEast() - b.getWest()) * 0.35;
      const dLat = (b.getNorth() - b.getSouth()) * 0.35;
      const box = [b.getWest() - dLng, b.getSouth() - dLat, b.getEast() + dLng, b.getNorth() + dLat];
      // Skip refetch if an already-loaded box fully covers this viewport.
      if (villageBoxesRef.current.some((lb) => lb[0] <= box[0] && lb[1] <= box[1] && lb[2] >= box[2] && lb[3] >= box[3])) return;
      villageBoxesRef.current.push(box);
      axios.get(`${API}/locations/geojson`, { params: { types: 'village', bbox: box.join(','), limit: 5000 } })
        .then(({ data }) => {
          const counts = placeCountsRef.current;
          let added = false;
          (data.features || []).forEach((f) => {
            const id = f.properties?.id || f.properties?.slug;
            if (!id || villageIdsRef.current.has(id)) return;
            villageIdsRef.current.add(id);
            f.properties.count = counts[`village:${f.properties?.slug}`] || 0;
            villagesFCRef.current.push(f);
            added = true;
          });
          if (added) {
            const src = map.getSource('villages');
            if (src) src.setData({ type: 'FeatureCollection', features: villagesFCRef.current });
          }
        })
        .catch(() => { villageBoxesRef.current.pop(); }); // allow retry on next pan
    };

    const onMoveEnd = () => {
      if (villageTimerRef.current) clearTimeout(villageTimerRef.current);
      villageTimerRef.current = setTimeout(loadVillages, 350);
    };

    if (wantPlaces.length) {
      // Cities + districts up front (small); villages come by bbox below.
      const upfrontTypes = [...new Set(wantPlaces.filter((p) => p.key !== 'villages').flatMap((p) => p.types))];
      const geoP = upfrontTypes.length
        ? axios.get(`${API}/locations/geojson`, { params: { types: upfrontTypes.join(',') } })
        : Promise.resolve({ data: { features: [] } });
      // Activity totals per place (city total incl. its districts/villages + region totals).
      const actP = axios.get(`${API}/locations/activity`).catch(() => ({ data: { features: [] } }));
      tasks.push(Promise.all([geoP, actP]).then(([{ data }, act]) => {
        const counts = {};
        const byCat = {};
        // Key by `loc_type:slug` — a slug alone is NOT unique across levels
        // (e.g. the Ventspils district "kurzeme" collides with the Kurzeme
        // region slug), which would otherwise copy the region total onto the
        // district bubble. Scoping by loc_type keeps each level's own numbers.
        (act.data.features || []).forEach((f) => {
          const k = `${f.properties?.loc_type}:${f.properties?.slug}`;
          counts[k] = f.properties?.count || 0;
          if (f.properties?.by_category) byCat[k] = f.properties.by_category;
        });
        placeCountsRef.current = counts;
        const buckets = Object.fromEntries(wantPlaces.map((cfg) => [cfg.key, []]));
        (data.features || []).forEach((f) => {
          const layerKey = typeToLayer[f.properties?.loc_type];
          if (layerKey && layerKey !== 'villages') {
            const k = `${f.properties?.loc_type}:${f.properties?.slug}`;
            f.properties.count = counts[k] || 0;
            if (layerKey === 'cities' || layerKey === 'districts') f.properties.by_category = byCat[k] || null;
            buckets[layerKey].push(f);
          }
        });
        wantPlaces.forEach((cfg) => {
          if (cfg.key === 'villages') return;
          const src = map.getSource(cfg.key);
          if (src) src.setData({ type: 'FeatureCollection', features: buckets[cfg.key] || [] });
        });
        citiesRef.current = buckets['cities'] || [];
        districtsRef.current = buckets['districts'] || [];
        // Level-0 region clusters (HTML markers) use the activity region features.
        regionsRef.current = (act.data.features || []).filter((f) => f.properties?.loc_type === 'region');
        renderRegionMarkers();
        renderCityMarkers();
        renderDistrictMarkers();
        // Villages for the current viewport (e.g. a deep-link that opens zoomed in).
        loadVillages();
      }));
      map.on('moveend', onMoveEnd);
    }
    if (wantCompanies) {
      tasks.push(axios.get(`${API}/map/businesses`).then(({ data }) => {
        companiesRef.current = data.features || [];
        applyBizFilters();
      }));
    }
    if (wantEvents) {
      tasks.push(axios.get(`${API}/map/events`).then(({ data }) => {
        eventsRef.current = data.features || [];
        applyEventFilters();
      }));
    }
    Promise.allSettled(tasks).finally(() => setLoadingData(false));
    return () => {
      map.off('moveend', onMoveEnd);
      if (villageTimerRef.current) clearTimeout(villageTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // ---- Classifieds 🧳 data feeder → the clustered 'cls' source renders them
  // as clustered HTML markers (see syncCls in map init). Priority: explorer
  // override (setClassifieds) → MapPage toggle (emoji extraMarkers) → ambient
  // auto-fetched geo. ----
  const applyClsData = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('cls');
    if (!src) return;
    const base = Array.isArray(clsOverrideRef.current) ? clsOverrideRef.current
      : (Array.isArray(extraClsRef.current) ? extraClsRef.current : (autoClsRef.current || []));
    const feats = base.filter((m) => m && m.lat != null && m.lng != null).map((m) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.lng, m.lat] },
      properties: {
        id: m.id || '', slug: m.slug || '', title: m.title || m.name || '',
        emoji: m.emoji || '🧳',
        link: m.link || ((m.slug || m.id) ? `/sludinajums/${m.slug || m.id}` : ''),
      },
    }));
    clsActiveRef.current = feats.length > 0;
    src.setData({ type: 'FeatureCollection', features: feats });
  }, []);
  useEffect(() => { applyClsDataRef.current = applyClsData; }, [applyClsData]);

  // Track the MapPage classifieds toggle (emoji extraMarkers) separately.
  useEffect(() => {
    const toggle = (extraMarkers || []).filter((m) => m && m.emoji && m.lat != null && m.lng != null);
    extraClsRef.current = toggle.length ? toggle : null;
    if (ready) applyClsData();
  }, [extraMarkers, ready, applyClsData]);

  // Fetch the ambient classifieds once the map is ready.
  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    if (autoClsRef.current.length) { applyClsData(); return undefined; }
    axios.get(`${API}/classifieds/geo`, { params: { limit: 1000 } })
      .then(({ data }) => { if (!cancelled) { autoClsRef.current = data || []; applyClsData(); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready, applyClsData]);
  const setLayerVis = (map, key, on) =>
    (LAYER_GROUPS[key] || []).forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });

  const EXCLUSIVE = ['companies', 'events'];

  const toggleLayer = (key) => {
    const map = mapRef.current;
    if (!map) return;

    // "Uzņēmumi" and "Pasākumi" are exclusive activity views: turning one on
    // hides every other layer (cities, districts, villages + the other activity)
    // to keep the view uncluttered, and restores the geography when toggled off.
    if (EXCLUSIVE.includes(key)) {
      const next = !enabled[key];
      if (next) {
        previousLayersRef.current = FILTERS
          .filter((f) => !EXCLUSIVE.includes(f.key) && enabled[f.key])
          .map((f) => f.key);
        setEnabled((prev) => {
          const upd = { ...prev };
          FILTERS.forEach((f) => { upd[f.key] = (f.key === key); });
          return upd;
        });
        FILTERS.forEach((f) => setLayerVis(map, f.key, f.key === key));
        if (key === 'companies') setBizPanelOpen(true); else setEventPanelOpen(true);
      } else {
        const prev = previousLayersRef.current;
        const restore = (prev && prev.length) ? prev : ['cities', 'districts', 'villages'];
        setEnabled((p) => {
          const upd = { ...p, [key]: false };
          restore.forEach((k) => { upd[k] = true; });
          return upd;
        });
        setLayerVis(map, key, false);
        restore.forEach((k) => setLayerVis(map, k, true));
        previousLayersRef.current = null;
        if (key === 'companies') { setBizPanelOpen(false); setBizSheetOpen(false); }
        else { setEventPanelOpen(false); setEventSheetOpen(false); }
      }
      return;
    }

    const next = !enabled[key];
    setEnabled((prev) => ({ ...prev, [key]: next }));
    setLayerVis(map, key, next);
    // Cities are represented by composite markers in the city band, so keep the
    // native point/count hidden there — prevents a one-frame duplicate circle
    // when the "Pilsētas" filter is re-enabled while zoomed to the city band.
    if (key === 'cities' && next && map.getZoom() >= REGION_MAX_ZOOM) {
      ['cities-point', 'cities-count'].forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none'); });
      if (map.getLayer('cities-label')) map.setLayoutProperty('cities-label', 'visibility', 'none');
    }
  };

  // ---- Business ("Uzņēmumi") layer client-side filtering ----
  const applyBizFilters = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('companies');
    if (!src) return;
    const f = bizFilters;
    const feats = (companiesRef.current || []).filter((ft) => {
      const p = ft.properties || {};
      const isPage = !!(p.template && p.template !== 'null' && p.template !== '');
      if (f.kind === 'pages' && !isPage) return false;
      if (f.kind === 'shops' && isPage) return false;
      if (f.activity.length && !f.activity.some((a) => (p.activity_types || []).includes(a))) return false;
      if (f.deal.length && !f.deal.some((d) => (p.deal_types || []).includes(d))) return false;
      if (f.openNow && !p.open_now) return false;
      if (f.delivery && !p.has_delivery) return false;
      if (f.topRated && !(typeof p.rating === 'number' && p.rating >= 4.5)) return false;
      if (f.freeSlots && !p.has_free_slots) return false;
      if (f.near && userLocRef.current) {
        const [lng, lat] = ft.geometry.coordinates;
        if (haversineM(userLocRef.current.lat, userLocRef.current.lng, lat, lng) > f.near) return false;
      }
      return true;
    });
    src.setData({ type: 'FeatureCollection', features: feats });
    setBizCount(feats.length);
    if (onResultsChangeRef.current) onResultsChangeRef.current(feats.map((f) => f.properties), enabled.companies, 'companies');
  }, [bizFilters, enabled.companies]);

  useEffect(() => { if (ready) applyBizFilters(); }, [bizFilters, ready, applyBizFilters]);

  // ---- Events ("Pasākumi") layer client-side filtering ----
  const applyEventFilters = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('events');
    if (!src) return;
    const f = eventFilters;
    const now = new Date();
    const today = now.toDateString();
    const feats = (eventsRef.current || []).filter((ft) => {
      const p = ft.properties || {};
      if (f.categories.length && !f.categories.includes(p.category)) return false;
      if (f.price === 'free' && p.is_paid) return false;
      if (f.price === 'paid' && !p.is_paid) return false;
      if (f.date !== 'all' && p.event_date) {
        const d = new Date(p.event_date);
        const days = (d - now) / 86400000;
        if (f.date === 'today' && d.toDateString() !== today) return false;
        if (f.date === 'week' && !(days >= -1 && days <= 7)) return false;
        if (f.date === 'month' && !(days >= -1 && days <= 31)) return false;
      }
      return true;
    });
    src.setData({ type: 'FeatureCollection', features: feats });
    setEventCount(feats.length);
    if (onResultsChangeRef.current) onResultsChangeRef.current(feats.map((f) => f.properties), enabled.events, 'events');
  }, [eventFilters, enabled.events]);

  useEffect(() => { if (ready) applyEventFilters(); }, [eventFilters, ready, applyEventFilters]);

  // ---- Level-0 region clusters (HTML markers) ----
  // Composite marker: an orange CENTRE with the region's grand total (sum of
  // all 5 categories) + up to 5 coloured category satellites (each with its own
  // count). Clicking the centre drills into the region; clicking a satellite
  // flies in AND focuses that single category.
  // Builds one category satellite chip around a place bubble. Classifieds (C2C)
  // render as the 🧳 suitcase ITSELF (white chip, teal ring) instead of a
  // coloured number dot — per user request — while still carrying the count in
  // its tooltip/aria. Zooming in past the city band swaps these aggregate
  // suitcases for the individual classified category pins (🚗🏡…) on the map.
  const satBtnHTML = ({ c, x, y, count, colors, testid, lbl }) => {
    if (c.key === 'classified') {
      return `<button type="button" class="kolv-region-sat kolv-region-sat-cls" data-cat="classified" data-testid="${testid}" title="${lbl}: ${count}" aria-label="${lbl}: ${count}" style="--x:${x}px;--y:${y}px"><span class="kolv-sat-emoji">🧳</span></button>`;
    }
    return `<button type="button" class="kolv-region-sat" data-cat="${c.key}" data-testid="${testid}" title="${lbl}: ${count}" aria-label="${lbl}: ${count}" style="--c:${colors[c.key] || '#999'};--x:${x}px;--y:${y}px"><span>${count}</span></button>`;
  };
  // Six category satellites evenly spaced (60° apart) around each place bubble.
  const REGION_SAT_CATS = [
    { key: 'shop', angle: -90, lv: 'Veikali', en: 'Shops', ru: 'Магазины' },
    { key: 'service', angle: -30, lv: 'Pakalpojumi', en: 'Services', ru: 'Услуги' },
    { key: 'event', angle: 30, lv: 'Pasākumi', en: 'Events', ru: 'События' },
    { key: 'project', angle: 90, lv: 'Projekti', en: 'Projects', ru: 'Проекты' },
    { key: 'business', angle: 150, lv: 'Uzņēmumi', en: 'Businesses', ru: 'Компании' },
    { key: 'classified', angle: 210, lv: 'Sludinājumi', en: 'Classifieds', ru: 'Объявления' },
  ];
  const renderRegionMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    regionMarkersRef.current.forEach((m) => m.remove());
    regionMarkersRef.current = [];
    const colors = catColorsRef.current;
    (regionsRef.current || []).forEach((f) => {
      const p = f.properties || {};
      const coords = f.geometry?.coordinates;
      if (!coords) return;
      const total = p.count || 0;
      const byCat = p.by_category || {};
      const cs = Math.round(Math.max(42, Math.min(62, 42 + Math.sqrt(total) * 2.6)));
      const ss = 24;
      const R = cs / 2 + ss / 2 + 3;
      const box = Math.round((R + ss / 2) * 2 + 6);
      const neutral = total === 0;

      const sats = REGION_SAT_CATS.filter((c) => (byCat[c.key] || 0) > 0).map((c) => {
        const rad = (c.angle * Math.PI) / 180;
        const x = Math.round(Math.cos(rad) * R);
        const y = Math.round(Math.sin(rad) * R);
        const lbl = isLV ? c.lv : (language === 'ru' ? c.ru : c.en);
        return satBtnHTML({ c, x, y, count: byCat[c.key], colors, testid: `region-sat-${p.slug}-${c.key}`, lbl });
      }).join('');

      const el = document.createElement('div');
      el.setAttribute('data-testid', `region-cluster-wrap-${p.slug}`);
      el.className = 'kolv-region-cluster';
      el.style.zIndex = '10';
      el.style.setProperty('--box', `${box}px`);
      el.style.setProperty('--cs', `${cs}px`);
      el.dataset.count = String(total);
      el.dataset.r = String(Math.round(cs / 2 + 6));
      el.innerHTML =
        `<span class="kolv-region-halo"${neutral ? ' style="opacity:0"' : ''}></span>` +
        sats +
        `<button type="button" class="kolv-region-center${neutral ? ' kolv-region-center-empty' : ''}" data-testid="region-cluster-${p.slug}">` +
          `<span class="kolv-region-count">${total}</span>` +
          `<span class="kolv-region-name">${p.name || ''}</span>` +
        `</button>`;

      // Zoom so EVERY city of the region is visible (phone view included):
      // fit the bounding box of the region's cities instead of a fixed zoom.
      // Never stay below the zoom where the region cluster hides and the city
      // bubbles take over, otherwise the big cluster would cover the cities.
      const fitRegionCities = () => {
        const rc = (citiesRef.current || []).filter(
          (c) => c.properties?.region_slug === p.slug && c.geometry?.coordinates);
        if (rc.length >= 2) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          rc.forEach((c) => {
            const [x, y] = c.geometry.coordinates;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          });
          const mobile = (map.getContainer()?.clientWidth || 999) < 640;
          const pad = mobile ? { top: 54, bottom: 84, left: 18, right: 18 } : 90;
          const cam = map.cameraForBounds([[minX, minY], [maxX, maxY]], { padding: pad, maxZoom: 9.2 });
          if (cam) {
            map.easeTo({ center: cam.center, zoom: Math.max(cam.zoom, REGION_MAX_ZOOM + 0.1), duration: 900 });
            return;
          }
        }
        map.easeTo({ center: coords, zoom: REGION_CLICK_ZOOM, duration: 900 });
      };

      const openRegion = () => {
        fitRegionCities();
        onRegionOpenRef.current?.({ slug: p.slug, name: p.name, coords });
        if (regionPopupRef.current) regionPopupRef.current.remove();
        const cityCount = p.city_count || 0;
        const openTxt = language === 'lv' ? `Atvērt ${p.name || ''} lapu` : language === 'ru' ? `Открыть страницу ${p.name || ''}` : `Open ${p.name || ''} page`;
        const actLbl = (language === 'lv' ? 'aktivitātes' : language === 'ru' ? 'активностей' : 'activities');
        const cityLbl = (language === 'lv' ? 'pilsētas' : language === 'ru' ? 'городов' : 'cities');
        const hint = (language === 'lv' ? 'Tuvini vēl, lai redzētu ciemus' : language === 'ru' ? 'Приблизьте, чтобы увидеть сёла' : 'Zoom in further to see villages');
        const html =
          `<div class="kolv-region-card" data-testid="region-card-${p.slug}">` +
            `<div class="kolv-region-card-head"><span class="kolv-region-card-name">${p.name || ''}</span></div>` +
            `<div class="kolv-region-card-stats">` +
              `<div class="kolv-region-stat"><span class="kolv-region-stat-num">${total}</span><span class="kolv-region-stat-lbl">${actLbl}</span></div>` +
              `<div class="kolv-region-stat"><span class="kolv-region-stat-num">${cityCount}</span><span class="kolv-region-stat-lbl">${cityLbl}</span></div>` +
            `</div>` +
            `<button type="button" class="kolv-region-offer" data-testid="region-offer-${p.slug}"><span>${openTxt}</span><span class="kolv-region-offer-arrow">→</span></button>` +
            `<div class="kolv-region-hint">${hint}</div>` +
          `</div>`;
        const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, offset: 20, className: 'kolv-region-popup', maxWidth: '230px' })
          .setLngLat(coords).setHTML(html).addTo(map);
        popup.getElement().querySelector('.kolv-region-offer')?.addEventListener('click', () => navigate(`/region/${p.slug}`));
        regionPopupRef.current = popup;
        regionPopupProtectRef.current = true;
      };

      el.querySelector('.kolv-region-center')?.addEventListener('click', (ev) => { ev.stopPropagation(); openRegion(); });
      el.querySelectorAll('.kolv-region-sat').forEach((sb) => {
        sb.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = sb.getAttribute('data-cat');
          fitRegionCities();
          if (regionPopupRef.current) { regionPopupRef.current.remove(); regionPopupRef.current = null; }
          onRegionOpenRef.current?.({ slug: p.slug, name: p.name, coords });
          onRegionCategoryRef.current?.(key, { slug: p.slug, name: p.name, coords });
        });
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat(coords).addTo(map);
      regionMarkersRef.current.push(marker);
    });
    const show = map.getZoom() < REGION_MAX_ZOOM;
    regionMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    scheduleDeclutterRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isLV, language, catColorsVersion]);

  // ---- City-level composite clusters (HTML markers) ----
  // Same composite look as regions, scaled down: an orange CENTRE with the
  // city's grand total + its coloured category satellites. Shown only in the
  // city zoom band (region band < zoom < district band), replacing the plain
  // count bubble. Clicking the centre zooms into the city; clicking a satellite
  // zooms in AND focuses that single category (same wiring as region satellites).
  const renderCityMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    cityMarkersRef.current.forEach((m) => m.remove());
    cityMarkersRef.current = [];
    const colors = catColorsRef.current;
    (citiesRef.current || []).forEach((f) => {
      const p = f.properties || {};
      const total = p.count || 0;
      const coords = f.geometry?.coordinates;
      if (!coords) return;
      // ALL cities of a region get a bubble (user request). Cities WITHOUT any
      // activity show a small grey circle with ONLY the name (no number, no
      // category satellites) — mirroring the empty-region style.
      const neutral = total <= 0;
      const byCat = p.by_category || {};
      const cs = Math.round(Math.max(28, Math.min(44, 28 + Math.sqrt(total) * 2.2)));
      const ss = 19;
      const R = cs / 2 + ss / 2 + 2;
      const box = Math.round((R + ss / 2) * 2 + 6);

      const sats = neutral ? '' : REGION_SAT_CATS.filter((c) => (byCat[c.key] || 0) > 0).map((c) => {
        const rad = (c.angle * Math.PI) / 180;
        const x = Math.round(Math.cos(rad) * R);
        const y = Math.round(Math.sin(rad) * R);
        const lbl = isLV ? c.lv : (language === 'ru' ? c.ru : c.en);
        return satBtnHTML({ c, x, y, count: byCat[c.key], colors, testid: `city-sat-${p.slug}-${c.key}`, lbl });
      }).join('');

      const el = document.createElement('div');
      el.setAttribute('data-testid', `city-cluster-wrap-${p.slug}`);
      el.className = 'kolv-region-cluster kolv-city-cluster';
      el.style.zIndex = neutral ? '8' : '9';
      el.style.setProperty('--box', `${box}px`);
      el.style.setProperty('--cs', `${cs}px`);
      el.dataset.count = String(total);
      el.dataset.r = String(Math.round(cs / 2 + 5));
      el.innerHTML =
        sats +
        `<button type="button" class="kolv-region-center kolv-city-center${neutral ? ' kolv-region-center-empty' : ''}" data-testid="city-cluster-${p.slug}">` +
          (neutral ? '' : `<span class="kolv-region-count">${total}</span>`) +
          `<span class="kolv-region-name">${p.name || ''}</span>` +
        `</button>`;

      const zoomIntoCity = () => {
        map.easeTo({ center: coords, zoom: Math.max(map.getZoom() + 0.7, 9.0), duration: 700 });
      };
      el.querySelector('.kolv-city-center')?.addEventListener('click', (ev) => { ev.stopPropagation(); zoomIntoCity(); });
      el.querySelectorAll('.kolv-region-sat').forEach((sb) => {
        sb.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = sb.getAttribute('data-cat');
          zoomIntoCity();
          onRegionOpenRef.current?.();
          onRegionCategoryRef.current?.(key, { slug: p.slug, name: p.name, coords });
        });
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat(coords).addTo(map);
      cityMarkersRef.current.push(marker);
    });
    const z = map.getZoom();
    const show = (enabledRef.current?.cities ?? true) && z >= REGION_MAX_ZOOM;
    cityMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    scheduleDeclutterRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLV, language, catColorsVersion]);

  // ---- District-level composite clusters (HTML markers) ----
  // Same composite look as cities but with a BLUE centre, shown from the
  // district band onward. ONLY districts WITH activity become a composite —
  // empty districts stay as the small grey native dots for geographic context.
  const renderDistrictMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    districtMarkersRef.current.forEach((m) => m.remove());
    districtMarkersRef.current = [];
    const colors = catColorsRef.current;
    (districtsRef.current || []).forEach((f) => {
      const p = f.properties || {};
      const total = p.count || 0;
      const coords = f.geometry?.coordinates;
      if (!coords || total <= 0) return; // empty districts remain native grey dots
      const byCat = p.by_category || {};
      const cs = Math.round(Math.max(24, Math.min(38, 24 + Math.sqrt(total) * 2)));
      const ss = 17;
      const R = cs / 2 + ss / 2 + 2;
      const box = Math.round((R + ss / 2) * 2 + 6);

      const sats = REGION_SAT_CATS.filter((c) => (byCat[c.key] || 0) > 0).map((c) => {
        const rad = (c.angle * Math.PI) / 180;
        const x = Math.round(Math.cos(rad) * R);
        const y = Math.round(Math.sin(rad) * R);
        const lbl = isLV ? c.lv : (language === 'ru' ? c.ru : c.en);
        return satBtnHTML({ c, x, y, count: byCat[c.key], colors, testid: `district-sat-${p.slug}-${c.key}`, lbl });
      }).join('');

      const el = document.createElement('div');
      el.setAttribute('data-testid', `district-cluster-wrap-${p.slug}`);
      el.className = 'kolv-region-cluster kolv-city-cluster kolv-district-cluster';
      el.style.zIndex = '7';
      el.style.setProperty('--box', `${box}px`);
      el.style.setProperty('--cs', `${cs}px`);
      el.dataset.count = String(total);
      el.dataset.r = String(Math.round(cs / 2 + 5));
      el.innerHTML =
        sats +
        `<button type="button" class="kolv-region-center kolv-city-center kolv-district-center" data-testid="district-cluster-${p.slug}">` +
          `<span class="kolv-region-count">${total}</span>` +
          `<span class="kolv-region-name">${p.name || ''}</span>` +
        `</button>`;

      const zoomIntoDistrict = () => {
        map.easeTo({ center: coords, zoom: Math.max(map.getZoom() + 0.7, 11.0), duration: 700 });
      };
      el.querySelector('.kolv-district-center')?.addEventListener('click', (ev) => { ev.stopPropagation(); zoomIntoDistrict(); });
      el.querySelectorAll('.kolv-region-sat').forEach((sb) => {
        sb.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const key = sb.getAttribute('data-cat');
          zoomIntoDistrict();
          onRegionOpenRef.current?.();
          onRegionCategoryRef.current?.(key, { slug: p.slug, name: p.name, coords });
        });
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat(coords).addTo(map);
      districtMarkersRef.current.push(marker);
    });
    const z = map.getZoom();
    const show = (enabledRef.current?.districts ?? true) && z >= DISTRICT_COMPOSITE_MIN;
    districtMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    scheduleDeclutterRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLV, language, catColorsVersion]);

  useEffect(() => { if (ready) { renderRegionMarkers(); renderCityMarkers(); renderDistrictMarkers(); } }, [isLV, ready, renderRegionMarkers, renderCityMarkers, renderDistrictMarkers]);

  // Toggle far-zoom HTML markers (regions + invest CTAs) as the user zooms.
  useEffect(() => {
    const show = currentZoom < REGION_MAX_ZOOM;
    regionMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    investMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
  }, [currentZoom]);

  // City composite clusters are the city marker from the city band onward — they
  // fully REPLACE the native city bubble (point + number + label). Once the user
  // zooms into the district/village bands the composite STAYS visible (on top of
  // the district circles) so the city keeps its summary bubble; the plain name
  // label is never used. The native bubble only lives in the region band (hidden
  // there via opacity).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const z = currentZoom;
    const showComposite = enabled.cities && z >= REGION_MAX_ZOOM;
    cityMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !showComposite));
    const setVis = (id, on) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); };
    if (enabled.cities) {
      setVis('cities-point', z < REGION_MAX_ZOOM); // hidden from the city band onward — composite replaces it
      setVis('cities-count', z < REGION_MAX_ZOOM);
      setVis('cities-label', false);               // composite carries the name at every zoom level
    }
  }, [currentZoom, enabled.cities, ready]);

  // District composite clusters: shown from the district band onward when the
  // "Mikrorajoni" layer is enabled. The native district layers (grey dots /
  // numbers / labels) are filtered to EMPTY districts only, so there is never a
  // duplicate circle for an active district.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const z = currentZoom;
    const show = enabled.districts && z >= DISTRICT_COMPOSITE_MIN;
    districtMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    scheduleDeclutterRef.current?.();
  }, [currentZoom, enabled.districts, ready]);

  const toggleEventCategory = (key) => setEventFilters((f) => ({
    ...f, categories: f.categories.includes(key) ? f.categories.filter((k) => k !== key) : [...f.categories, key],
  }));
  const clearEventFilters = () => setEventFilters({ categories: [], price: 'all', date: 'all' });
  const activeEventFilterCount = eventFilters.categories.length + (eventFilters.price !== 'all' ? 1 : 0) + (eventFilters.date !== 'all' ? 1 : 0);

  const ensureUserLoc = () => new Promise((resolve) => {
    if (userLocRef.current) return resolve(userLocRef.current);
    if (!('geolocation' in navigator)) { toast.error((language === 'lv' ? 'Ierīce neatbalsta atrašanās vietu' : language === 'ru' ? 'Геолокация не поддерживается' : 'Geolocation not supported')); return resolve(null); }
    navigator.geolocation.getCurrentPosition(
      (pos) => { userLocRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; resolve(userLocRef.current); },
      () => { toast.error((language === 'lv' ? 'Neizdevās noteikt atrašanās vietu' : language === 'ru' ? 'Не удалось определить местоположение' : 'Could not get location')); resolve(null); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });

  const toggleArrFilter = (group, key) => setBizFilters((f) => ({
    ...f, [group]: f[group].includes(key) ? f[group].filter((x) => x !== key) : [...f[group], key],
  }));
  const setNear = async (m) => {
    const next = bizFilters.near === m ? 0 : m;
    if (next) { const loc = await ensureUserLoc(); if (!loc) return; }
    setBizFilters((f) => ({ ...f, near: next }));
  };
  const clearBizFilters = () => setBizFilters({ kind: 'all', activity: [], deal: [], openNow: false, delivery: false, topRated: false, freeSlots: false, near: 0 });
  const activeBizFilterCount = (bizFilters.kind !== 'all' ? 1 : 0) + bizFilters.activity.length + bizFilters.deal.length + (bizFilters.openNow ? 1 : 0) + (bizFilters.delivery ? 1 : 0) + (bizFilters.topRated ? 1 : 0) + (bizFilters.freeSlots ? 1 : 0) + (bizFilters.near ? 1 : 0);

  const filterControls = (
    <div className="space-y-3">
      {/* Company kind: business pages vs shops */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Veids' : language === 'ru' ? 'Тип' : 'Type')}</p>
        <div className="inline-flex items-center p-1 rounded-full bg-[#F3F2EE] border border-[#E5E4E0]" data-testid="biz-kind-toggle">
          {[
            { key: 'all',   lv: 'Visi',            en: 'All', ru: 'Все' },
            { key: 'pages', lv: 'Uzņēmumu lapas',  en: 'Business pages', ru: 'Страницы компаний' },
            { key: 'shops', lv: 'Veikali',         en: 'Shops', ru: 'Магазины' },
          ].map((o) => {
            const on = bizFilters.kind === o.key;
            return (
              <button key={o.key} type="button" onClick={() => setBizFilters((f) => ({ ...f, kind: o.key }))}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${on ? 'bg-[#7C3AED] text-white shadow-sm' : 'text-[#5C5C5C] hover:text-[#121212]'}`}
                data-testid={`biz-kind-${o.key}`}>
                {(language === 'ru' ? (o.ru || o.en) : isLV ? o.lv : o.en)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Activity type */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Ko vēlies darīt?' : language === 'ru' ? 'Что вы хотите?' : 'What do you want?')}</p>
        <div className="flex flex-wrap gap-1.5">
          {ACTIVITY_OPTS.map((o) => {
            const on = bizFilters.activity.includes(o.key);
            return (
              <button key={o.key} type="button" onClick={() => toggleArrFilter('activity', o.key)}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${on ? 'text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
                style={on ? { backgroundColor: o.color } : {}}
                data-testid={`biz-activity-${o.key}`}>
                {(language === 'ru' ? (o.ru || o.en) : isLV ? o.lv : o.en)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Deal type */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Darījuma veids' : language === 'ru' ? 'Тип сделки' : 'Deal type')}</p>
        <div className="flex flex-wrap gap-1.5">
          {DEAL_OPTS.map((o) => {
            const on = bizFilters.deal.includes(o.key);
            return (
              <button key={o.key} type="button" onClick={() => toggleArrFilter('deal', o.key)}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${on ? 'bg-[#121212] text-white border-[#121212]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
                data-testid={`biz-deal-${o.key}`}>
                {(language === 'ru' ? (o.ru || o.en) : isLV ? o.lv : o.en)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic toggles */}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setBizFilters((f) => ({ ...f, openNow: !f.openNow }))}
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all ${bizFilters.openNow ? 'bg-[#16A34A] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
          data-testid="biz-open-now">
          <Clock className="w-3.5 h-3.5" /> {(language === 'lv' ? 'Atvērts tagad' : language === 'ru' ? 'Открыто сейчас' : 'Open now')}
        </button>
        <button type="button" onClick={() => setBizFilters((f) => ({ ...f, delivery: !f.delivery }))}
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all ${bizFilters.delivery ? 'bg-[#2563EB] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
          data-testid="biz-delivery">
          <Truck className="w-3.5 h-3.5" /> {(language === 'lv' ? 'Piegāde / Līdzņemšana' : language === 'ru' ? 'Доставка / Самовывоз' : 'Delivery / Pickup')}
        </button>
        <button type="button" onClick={() => setBizFilters((f) => ({ ...f, topRated: !f.topRated }))}
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all ${bizFilters.topRated ? 'bg-[#F59E0B] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
          data-testid="biz-top-rated">
          <Star className="w-3.5 h-3.5" /> {(language === 'lv' ? 'Top (4.5+)' : language === 'ru' ? 'Топ (4.5+)' : 'Top (4.5+)')}
        </button>
        <button type="button" onClick={() => setBizFilters((f) => ({ ...f, freeSlots: !f.freeSlots }))}
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all ${bizFilters.freeSlots ? 'bg-[#EC4899] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
          data-testid="biz-free-slots">
          <CalendarCheck className="w-3.5 h-3.5" /> {(language === 'lv' ? 'Ir brīvas vietas' : language === 'ru' ? 'Есть свободные места' : 'Has free slots')}
        </button>
      </div>

      {/* Distance */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5 flex items-center gap-1"><Navigation className="w-3 h-3" /> {(language === 'lv' ? 'Manā tuvumā' : language === 'ru' ? 'Рядом со мной' : 'Near me')}</p>
        <div className="flex flex-wrap gap-1.5">
          {NEAR_OPTS.map((o) => (
            <button key={o.m} type="button" onClick={() => setNear(o.m)}
              className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${bizFilters.near === o.m ? 'bg-[#7C3AED] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
              data-testid={`biz-near-${o.m}`}>
              {o.l}
            </button>
          ))}
          {activeBizFilterCount > 0 && (
            <button type="button" onClick={clearBizFilters} className="text-xs px-2.5 py-1.5 rounded-full text-[#DC2626] hover:bg-red-50 ml-auto" data-testid="biz-clear-filters">
              {(language === 'lv' ? 'Notīrīt' : language === 'ru' ? 'Очистить' : 'Clear')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const eventFilterControls = (
    <div className="space-y-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Cena' : language === 'ru' ? 'Цена' : 'Price')}</p>
        <div className="inline-flex items-center p-1 rounded-full bg-[#F3F2EE] border border-[#E5E4E0]" data-testid="event-price-toggle">
          {[{ key: 'all', lv: 'Visi', en: 'All', ru: 'Все' }, { key: 'free', lv: 'Bezmaksas', en: 'Free', ru: 'Бесплатно' }, { key: 'paid', lv: 'Maksas', en: 'Paid', ru: 'Платно' }].map((o) => {
            const on = eventFilters.price === o.key;
            return (
              <button key={o.key} type="button" onClick={() => setEventFilters((f) => ({ ...f, price: o.key }))}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${on ? 'bg-[#E11D48] text-white shadow-sm' : 'text-[#5C5C5C] hover:text-[#121212]'}`}
                data-testid={`event-price-${o.key}`}>{(language === 'ru' ? (o.ru || o.en) : isLV ? o.lv : o.en)}</button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Kad' : language === 'ru' ? 'Когда' : 'When')}</p>
        <div className="inline-flex items-center p-1 rounded-full bg-[#F3F2EE] border border-[#E5E4E0]" data-testid="event-date-toggle">
          {[{ key: 'all', lv: 'Jebkad', en: 'Any', ru: 'Любое' }, { key: 'today', lv: 'Šodien', en: 'Today', ru: 'Сегодня' }, { key: 'week', lv: 'Nedēļa', en: 'Week', ru: 'Неделя' }, { key: 'month', lv: 'Mēnesis', en: 'Month', ru: 'Месяц' }].map((o) => {
            const on = eventFilters.date === o.key;
            return (
              <button key={o.key} type="button" onClick={() => setEventFilters((f) => ({ ...f, date: o.key }))}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${on ? 'bg-[#E11D48] text-white shadow-sm' : 'text-[#5C5C5C] hover:text-[#121212]'}`}
                data-testid={`event-date-${o.key}`}>{(language === 'ru' ? (o.ru || o.en) : isLV ? o.lv : o.en)}</button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">{(language === 'lv' ? 'Kategorija' : language === 'ru' ? 'Категория' : 'Category')}</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(EVENT_CATEGORIES).map(([k, c]) => {
            const on = eventFilters.categories.includes(k);
            return (
              <button key={k} type="button" onClick={() => toggleEventCategory(k)}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${on ? 'bg-[#E11D48] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
                data-testid={`event-cat-${k}`}>{(language === 'ru' ? (c.ru || c.en || c.lv) : isLV ? c.lv : (c.en || c.lv))}</button>
            );
          })}
          {activeEventFilterCount > 0 && (
            <button type="button" onClick={clearEventFilters} className="text-xs px-2.5 py-1.5 rounded-full text-[#DC2626] hover:bg-red-50 ml-auto" data-testid="event-clear-filters">
              {(language === 'lv' ? 'Notīrīt' : language === 'ru' ? 'Очистить' : 'Clear')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Search ----
  const runSearch = useCallback((text) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!text || text.trim().length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/locations`, { params: { q: text.trim(), limit: 7 } });
        const locResults = (data.locations || [])
          .filter((l) => l.lat != null && l.lng != null)
          .map((l) => ({ kind: 'location', name: l.name, sub: l.type, lng: l.lng, lat: l.lat, slug: l.slug }));
        const norm = text.trim().toLowerCase();
        const compResults = companiesRef.current
          .filter((f) => (f.properties?.name || '').toLowerCase().includes(norm))
          .slice(0, 5)
          .map((f) => ({ kind: 'company', name: f.properties.name, sub: f.properties.category,
            lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], id: f.properties.id }));
        setResults([...compResults, ...locResults].slice(0, 10));
      } catch (_) { setResults([]); }
      finally { setSearching(false); }
    }, 220);
  }, []);

  const pickResult = (r) => {
    setResults([]); setQuery(r.name);
    const map = mapRef.current;
    if (map) map.flyTo({ center: [r.lng, r.lat], zoom: r.kind === 'company' ? 13 : 11, duration: 900 });
    if (popupRef.current) popupRef.current.remove();
    popupRef.current = new maplibregl.Popup({ offset: 14, closeButton: true })
      .setLngLat([r.lng, r.lat])
      .setHTML(`<div style="font-family:Outfit,sans-serif;font-weight:600;color:#121212">${r.name}</div>`)
      .addTo(map);
  };

  // ---- "Find nearest to me" — device geolocation + fly-to + you-are-here pin ----
  const locateMe = () => {
    const map = mapRef.current;
    if (!map) return;
    if (!('geolocation' in navigator)) {
      toast.error((language === 'lv' ? 'Tava ierīce neatbalsta atrašanās vietu' : language === 'ru' ? 'Геолокация не поддерживается' : 'Geolocation not supported'));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude, longitude } = pos.coords;
        map.flyTo({ center: [longitude, latitude], zoom: 12, duration: 1200 });
        if (userMarkerRef.current) userMarkerRef.current.remove();
        userMarkerRef.current = new maplibregl.Marker({ color: '#2563EB' })
          .setLngLat([longitude, latitude]).addTo(map);
        onLocateRef.current?.({ lat: latitude, lng: longitude });
      },
      () => {
        setLocating(false);
        toast.error((language === 'lv' ? 'Neizdevās noteikt tavu atrašanās vietu' : language === 'ru' ? 'Не удалось определить ваше местоположение' : 'Could not get your location'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Neighbor-country invest markers. DESKTOP: small flag badge anchored deep
  // inside Estonia / Lithuania (away from the Latvian border) that expands to a
  // full invest card popup on click. MOBILE: hidden on the map — a fixed bottom
  // bar (below) handles the CTA so the Latvian map stays uncluttered.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    const GOAL = 10000;
    const COUNTRIES = [
      { campaign: 'region_ee', name_lv: 'Igaunija', name_en: 'Estonia', name_ru: 'Эстония', flag: '🇪🇪', lngLat: [25.0136, 58.5953] },
      { campaign: 'region_lt', name_lv: 'Lietuva', name_en: 'Lithuania', name_ru: 'Литва', flag: '🇱🇹', lngLat: [23.8813, 55.1694] },
    ];

    const openInvestCard = (c, name, progress, lngLat) => {
      const pc = (progress?.per_campaign || {})[c.campaign] || { total: 0, count: 0 };
      const raised = Math.round(pc.total || 0);
      const backers = pc.count || 0;
      const pct = Math.max(3, Math.min(100, Math.round((raised / GOAL) * 100)));
      if (investPopupRef.current) investPopupRef.current.remove();
      const html =
        `<div class="kolv-invest-card" data-testid="invest-card-${c.campaign}">` +
          `<div class="kolv-invest-card-top"><span class="kolv-invest-card-flag">${c.flag}</span>` +
            `<div><div class="kolv-invest-card-kicker">${(language === 'lv' ? 'Nākamā valsts' : language === 'ru' ? 'Следующая страна' : 'Next country')}</div>` +
            `<div class="kolv-invest-card-title">${language === 'lv' ? `Atbloķē ${name}` : language === 'ru' ? `Разблокируй ${name}` : `Unlock ${name}`}</div></div></div>` +
          `<div class="kolv-invest-card-desc">${(language === 'lv' ? 'Kļūsti par pirmo investoru un līdzattīsti KoLV šeit.' : language === 'ru' ? 'Станьте первым инвестором и помогите развивать KoLV здесь.' : 'Be a first investor and help build KoLV here.')}</div>` +
          `<div class="kolv-invest-card-bar"><div class="kolv-invest-card-fill" style="width:${pct}%"></div></div>` +
          `<div class="kolv-invest-card-meta">${(language === 'lv' ? 'Savākti' : language === 'ru' ? 'Собрано' : 'Raised')} €${raised.toLocaleString('lv-LV')} / €${GOAL.toLocaleString('lv-LV')} · ${backers > 0 ? `${backers} ${(language === 'lv' ? 'atbalstītāji' : language === 'ru' ? 'инвесторов' : 'backers')}` : ((language === 'lv' ? 'esi pirmais!' : language === 'ru' ? 'будьте первым!' : 'be first!'))}</div>` +
          `<button type="button" class="kolv-invest-card-btn" data-testid="invest-card-cta-${c.campaign}">${(language === 'lv' ? 'Investēt / Atbloķēt' : language === 'ru' ? 'Инвестировать / Разблокировать' : 'Invest / Unlock')}</button>` +
        `</div>`;
      const popup = new maplibregl.Popup({ offset: 16, closeButton: true, closeOnClick: true, className: 'kolv-invest-popup', maxWidth: '260px' })
        .setLngLat(lngLat).setHTML(html).addTo(map);
      popup.getElement().querySelector('.kolv-invest-card-btn')?.addEventListener('click', () => setRegionInvest({ campaign: c.campaign, name }));
      investPopupRef.current = popup;
    };

    const build = (progress) => {
      investMarkersRef.current.forEach((m) => m.remove());
      investMarkersRef.current = [];
      COUNTRIES.forEach((c) => {
        const name = language === 'lv' ? c.name_lv : language === 'ru' ? (c.name_ru || c.name_en) : c.name_en;
        const el = document.createElement('button');
        el.type = 'button';
        el.setAttribute('data-testid', `${c.campaign === 'region_ee' ? 'estonia' : 'lithuania'}-unlock-badge`);
        el.setAttribute('aria-label', language === 'lv' ? `Atbloķēt ${name}` : language === 'ru' ? `Разблокировать ${name}` : `Unlock ${name}`);
        el.title = language === 'lv' ? `Atbloķēt ${name}` : language === 'ru' ? `Разблокировать ${name}` : `Unlock ${name}`;
        el.className = 'kolv-unlock-btn';
        el.style.zIndex = '2';
        el.innerHTML = `<span class="kolv-unlock-flag">${c.flag}</span><span class="kolv-unlock-name">${name}</span><span class="kolv-unlock-plus">+</span>`;
        el.onclick = (ev) => { ev.stopPropagation(); openInvestCard(c, name, progress, c.lngLat); };
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat(c.lngLat).addTo(map);
        investMarkersRef.current.push(marker);
      });
      const show = map.getZoom() < REGION_MAX_ZOOM;
      investMarkersRef.current.forEach((m) => m.getElement()?.classList.toggle('kolv-hidden', !show));
    };

    if (investProgressRef.current) {
      build(investProgressRef.current);
    } else {
      axios.get(`${API}/investments/progress`).then(({ data }) => {
        if (cancelled) return;
        investProgressRef.current = data;
        build(data);
      }).catch(() => { if (!cancelled) build(null); });
    }
    return () => {
      cancelled = true;
      if (investPopupRef.current) { investPopupRef.current.remove(); investPopupRef.current = null; }
      investMarkersRef.current.forEach((m) => m.remove());
      investMarkersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLV]);




  return (
    <div className={`relative w-full overflow-hidden rounded-2xl border border-[#E5E4E0] bg-[#EEF1ED] ${className}`} style={{ height }} data-testid="vector-map">
      <div ref={mapContainer} className="w-full h-full" data-testid="vector-map-canvas" />

      {/* Zoom-level legend — shows which geography band is currently visible. */}
      {showFilters && wantPlaces.length > 0 && (
        <div className="absolute bottom-5 left-3 z-10 hidden sm:block" data-testid="map-zoom-legend">
          <div className="bg-white/90 backdrop-blur-md border border-[#E5E4E0] shadow-xl rounded-xl p-3 w-[190px]">
            <p className="text-[10px] tracking-[0.12em] uppercase font-semibold text-[#9CA3AF] mb-2">
              {(language === 'lv' ? 'Tuvinājuma līmenis' : language === 'ru' ? 'Уровень масштаба' : 'Zoom level')}
            </p>
            <div className="space-y-1">
              {[
                { lv: 'Reģioni (tālu)', en: 'Regions (far)', ru: 'Регионы (далеко)', color: '#D97736' },
                { lv: 'Pilsētas', en: 'Cities', ru: 'Города', color: '#D97736' },
                { lv: 'Pilsētu rajoni', en: 'City districts', ru: 'Городские районы', color: '#2563EB' },
                { lv: 'Ciemi (tuvu)', en: 'Villages (close)', ru: 'Сёла (близко)', color: '#4A6B53' },
              ].map((lvl, i) => {
                const active = zoomLevelIndex === i;
                return (
                  <div key={i}
                    className={`flex items-center gap-2 pl-2 py-1 rounded-md border-l-2 transition-colors duration-200 ${active ? 'bg-[#F3F2EE]' : 'border-transparent opacity-45'}`}
                    style={active ? { borderColor: lvl.color } : undefined}
                    data-testid={`zoom-legend-level-${i}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: lvl.color }} />
                    <span className={`text-[11px] leading-tight ${active ? 'font-semibold text-[#121212]' : 'text-[#5C5C5C]'}`}>
                      {(language === 'ru' ? (lvl.ru || lvl.en) : isLV ? lvl.lv : lvl.en)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <InvestModal
        target={regionInvest}
        onClose={() => setRegionInvest(null)}
      />


      {showSearch && (
        <div className="absolute top-3 left-3 z-10 w-[min(340px,calc(100%-6rem))]" data-testid="map-search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
              placeholder={(language === 'lv' ? 'Meklēt pilsētu, ciemu, uzņēmumu…' : language === 'ru' ? 'Поиск города, села, компании…' : 'Search city, village, company…')}
              className="w-full h-10 pl-9 pr-9 rounded-xl bg-white/95 backdrop-blur border border-[#E5E4E0] shadow-sm text-sm focus:outline-none focus:border-[#D97736]"
              data-testid="map-search-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#D97736]" />}
            {!searching && query && (
              <button type="button" onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#121212]" data-testid="map-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {results.length > 0 && (
            <div className="mt-1 bg-white border border-[#E5E4E0] rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto" data-testid="map-search-results">
              {results.map((r, i) => (
                <button
                  key={`${r.kind}-${r.slug || r.id || ''}-${i}`}
                  type="button"
                  onClick={() => pickResult(r)}
                  className="w-full text-left px-3 py-2 hover:bg-[#F3F2EE] flex items-center gap-2.5 border-b border-[#F0EFEB] last:border-0"
                  data-testid={`map-search-result-${i}`}
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${r.kind === 'company' ? 'bg-[#7C3AED]/10' : 'bg-[#D97736]/10'}`}>
                    {r.kind === 'company' ? <Building2 className="w-3.5 h-3.5 text-[#7C3AED]" /> : <MapPin className="w-3.5 h-3.5 text-[#D97736]" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-[#121212] truncate">{r.name}</span>
                    <span className="block text-[11px] text-[#5C5C5C] capitalize">{r.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showLocate && results.length === 0 && (
        <button
          type="button"
          onClick={locateMe}
          disabled={locating}
          className="absolute left-3 top-[3.5rem] z-[5] flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-[#E5E4E0] shadow-sm text-xs font-medium text-[#121212] hover:border-[#D97736] transition-colors disabled:opacity-60"
          data-testid="map-locate-btn"
          aria-label={(language === 'lv' ? 'Atrodi man tuvākos' : language === 'ru' ? 'Найти ближайшее' : 'Find nearest to me')}
        >
          {locating
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D97736]" />
            : <LocateFixed className="w-3.5 h-3.5 text-[#2563EB]" />}
          <span className="hidden sm:inline">{(language === 'lv' ? 'Atrodi man tuvākos' : language === 'ru' ? 'Найти ближайшее' : 'Find nearest to me')}</span>
          <span className="sm:hidden">{(language === 'lv' ? 'Tuvākie' : language === 'ru' ? 'Рядом' : 'Nearby')}</span>
        </button>
      )}

      {/* Business ("Uzņēmumi") filters — appear when the layer is on.
          Desktop: a card above the chips. Mobile: a floating pill that opens a
          bottom-sheet drawer so the map pins stay visible. */}
      {showFilters && enabledLayers.includes('companies') && enabled.companies && !isMobile && (
        <div className="absolute left-3 bottom-14 w-[380px] z-20" data-testid="biz-filter-panel">
          <div className="bg-white/97 backdrop-blur border border-[#E5E4E0] rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setBizPanelOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-2.5 border-b border-[#F0EFEB]"
              data-testid="biz-filter-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#121212]">
                <SlidersHorizontal className="w-4 h-4 text-[#7C3AED]" />
                {(language === 'lv' ? 'Uzņēmumu filtri' : language === 'ru' ? 'Фильтры компаний' : 'Business filters')}
                {activeBizFilterCount > 0 && (
                  <span className="text-[10px] font-bold bg-[#7C3AED] text-white rounded-full px-1.5 py-0.5">{activeBizFilterCount}</span>
                )}
              </span>
              <span className="text-[11px] text-[#5C5C5C]">{bizCount} {(language === 'lv' ? 'atrasti' : language === 'ru' ? 'найдено' : 'found')} {bizPanelOpen ? '▾' : '▸'}</span>
            </button>
            {bizPanelOpen && (
              <div className="p-3 max-h-[46vh] overflow-y-auto" data-testid="biz-filter-body">
                {filterControls}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile floating pill + bottom-sheet */}
      {showFilters && enabledLayers.includes('companies') && enabled.companies && isMobile && (
        <>
          <button
            type="button"
            onClick={() => setBizSheetOpen(true)}
            className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#7C3AED] text-white text-sm font-semibold shadow-lg"
            data-testid="biz-filter-mobile-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {(language === 'lv' ? 'Filtri' : language === 'ru' ? 'Фильтры' : 'Filters')}
            <span className="text-[11px] font-bold bg-white/25 rounded-full px-1.5 py-0.5">{bizCount}</span>
            {activeBizFilterCount > 0 && <span className="w-2 h-2 rounded-full bg-amber-300" />}
          </button>
          {bizSheetOpen && (
            <div className="absolute inset-0 z-40" data-testid="biz-filter-panel">
              <div className="absolute inset-0 bg-black/30" onClick={() => setBizSheetOpen(false)} />
              <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl max-h-[64%] overflow-y-auto p-4 shadow-2xl" data-testid="biz-filter-sheet">
                <div className="w-10 h-1 rounded-full bg-[#E5E4E0] mx-auto mb-3" />
                <div className="flex items-center justify-between mb-3">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#121212]">
                    <SlidersHorizontal className="w-4 h-4 text-[#7C3AED]" />
                    {(language === 'lv' ? 'Uzņēmumu filtri' : language === 'ru' ? 'Фильтры компаний' : 'Business filters')}
                    <span className="text-[11px] text-[#5C5C5C] font-normal">· {bizCount} {(language === 'lv' ? 'atrasti' : language === 'ru' ? 'найдено' : 'found')}</span>
                  </span>
                  <button type="button" onClick={() => setBizSheetOpen(false)} className="text-[#5C5C5C] hover:text-[#121212]" data-testid="biz-filter-sheet-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div data-testid="biz-filter-body">{filterControls}</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Events ("Pasākumi") filters — appear when the layer is on. */}
      {showFilters && enabledLayers.includes('events') && enabled.events && !isMobile && (
        <div className="absolute left-3 bottom-14 w-[380px] z-20" data-testid="event-filter-panel">
          <div className="bg-white/97 backdrop-blur border border-[#E5E4E0] rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setEventPanelOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-2.5 border-b border-[#F0EFEB]"
              data-testid="event-filter-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#121212]">
                <CalendarDays className="w-4 h-4 text-[#E11D48]" />
                {(language === 'lv' ? 'Pasākumu filtri' : language === 'ru' ? 'Фильтры мероприятий' : 'Event filters')}
                {activeEventFilterCount > 0 && (
                  <span className="text-[10px] font-bold bg-[#E11D48] text-white rounded-full px-1.5 py-0.5">{activeEventFilterCount}</span>
                )}
              </span>
              <span className="text-[11px] text-[#5C5C5C]">{eventCount} {(language === 'lv' ? 'atrasti' : language === 'ru' ? 'найдено' : 'found')} {eventPanelOpen ? '▾' : '▸'}</span>
            </button>
            {eventPanelOpen && (
              <div className="p-3 max-h-[46vh] overflow-y-auto" data-testid="event-filter-body">
                {eventFilterControls}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile events floating pill + bottom-sheet */}
      {showFilters && enabledLayers.includes('events') && enabled.events && isMobile && (
        <>
          <button
            type="button"
            onClick={() => setEventSheetOpen(true)}
            className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#E11D48] text-white text-sm font-semibold shadow-lg"
            data-testid="event-filter-mobile-toggle"
          >
            <CalendarDays className="w-4 h-4" />
            {(language === 'lv' ? 'Filtri' : language === 'ru' ? 'Фильтры' : 'Filters')}
            <span className="text-[11px] font-bold bg-white/25 rounded-full px-1.5 py-0.5">{eventCount}</span>
            {activeEventFilterCount > 0 && <span className="w-2 h-2 rounded-full bg-amber-300" />}
          </button>
          {eventSheetOpen && (
            <div className="absolute inset-0 z-40" data-testid="event-filter-panel">
              <div className="absolute inset-0 bg-black/30" onClick={() => setEventSheetOpen(false)} />
              <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl max-h-[64%] overflow-y-auto p-4 shadow-2xl" data-testid="event-filter-sheet">
                <div className="w-10 h-1 rounded-full bg-[#E5E4E0] mx-auto mb-3" />
                <div className="flex items-center justify-between mb-3">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#121212]">
                    <CalendarDays className="w-4 h-4 text-[#E11D48]" />
                    {(language === 'lv' ? 'Pasākumu filtri' : language === 'ru' ? 'Фильтры мероприятий' : 'Event filters')}
                    <span className="text-[11px] text-[#5C5C5C] font-normal">· {eventCount} {(language === 'lv' ? 'atrasti' : language === 'ru' ? 'найдено' : 'found')}</span>
                  </span>
                  <button type="button" onClick={() => setEventSheetOpen(false)} className="text-[#5C5C5C] hover:text-[#121212]" data-testid="event-filter-sheet-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div data-testid="event-filter-body">{eventFilterControls}</div>
              </div>
            </div>
          )}
        </>
      )}

      {showFilters && (
        <div className="absolute bottom-3 left-3 z-10 flex flex-wrap items-center gap-1.5 max-w-[calc(100%-1.5rem)]" data-testid="map-filters">
          {FILTERS.filter((f) => enabledLayers.includes(f.key) && f.group !== 'activity').map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => toggleLayer(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${enabled[f.key] ? 'bg-white shadow-sm border-[#E5E4E0] text-[#121212]' : 'bg-white/50 border-transparent text-[#9CA3AF] line-through'}`}
              data-testid={`map-filter-${f.key}`}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: enabled[f.key] ? f.color : '#C7C7C7' }} />
              {(language === 'ru' ? (f.labelRU || f.labelEN) : isLV ? f.labelLV : f.labelEN)}
            </button>
          ))}
        </div>
      )}

      {loadingData && (
        <div className="absolute top-3 right-14 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 border border-[#E5E4E0] shadow-sm text-xs text-[#5C5C5C]" data-testid="map-data-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D97736]" />
          {(language === 'lv' ? 'Ielādē…' : language === 'ru' ? 'Загрузка…' : 'Loading…')}
        </div>
      )}
    </div>
  );
});
